/**
 * Rules Engine Admin UI — Phase 1
 * Tabs: Artifacts | Sets | Traces
 */
import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from './config';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function authHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

async function apiFetch(path, opts = {}) {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    headers: authHeaders(),
    ...opts,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail));
  }
  return res.status === 204 ? null : res.json();
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const S = {
  shell: {
    display: 'flex', flexDirection: 'column', height: '100%',
    background: 'var(--bg-primary)', color: 'var(--text-primary)',
  },
  header: {
    padding: '20px 28px 0', borderBottom: '1px solid var(--border-color)',
    display: 'flex', flexDirection: 'column', gap: '4px',
  },
  title: { fontSize: '18px', fontWeight: 700, margin: 0 },
  tabs: { display: 'flex', gap: '2px', marginTop: '12px' },
  tab: (active) => ({
    padding: '8px 18px', borderRadius: '6px 6px 0 0', cursor: 'pointer',
    fontSize: '13px', fontWeight: 600, border: 'none',
    background: active ? 'var(--bg-secondary)' : 'transparent',
    color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
    borderBottom: active ? '2px solid var(--accent-color)' : '2px solid transparent',
    transition: 'all 100ms',
  }),
  body: { flex: 1, overflowY: 'auto', padding: '24px 28px' },
  row: { display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' },
  btn: (variant = 'primary') => ({
    padding: '7px 16px', borderRadius: '6px', cursor: 'pointer',
    fontWeight: 600, fontSize: '13px',
    background: variant === 'primary' ? 'var(--accent-color)'
      : variant === 'danger' ? '#ef4444'
      : variant === 'ghost' ? 'transparent'
      : 'var(--bg-secondary)',
    color: variant === 'primary' || variant === 'danger' ? '#fff' : 'var(--text-primary)',
    border: variant === 'ghost' ? '1px solid var(--border-color)' : 'none',
  }),
  card: {
    background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
    borderRadius: '8px', padding: '14px 18px', marginBottom: '8px',
    display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer',
    transition: 'border-color 150ms',
  },
  badge: (color) => ({
    fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px',
    background: color + '22', color, border: `1px solid ${color}55`,
    whiteSpace: 'nowrap',
  }),
  label: { fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px', display: 'block' },
  input: {
    width: '100%', padding: '8px 10px', borderRadius: '6px',
    border: '1px solid var(--border-color)', background: 'var(--bg-input)',
    color: 'var(--text-primary)', fontSize: '13px', boxSizing: 'border-box', outline: 'none',
  },
  textarea: {
    width: '100%', padding: '8px 10px', borderRadius: '6px',
    border: '1px solid var(--border-color)', background: 'var(--bg-input)',
    color: 'var(--text-primary)', fontSize: '13px', boxSizing: 'border-box',
    outline: 'none', fontFamily: 'monospace', resize: 'vertical', minHeight: '60px',
  },
  select: {
    padding: '8px 10px', borderRadius: '6px',
    border: '1px solid var(--border-color)', background: 'var(--bg-input)',
    color: 'var(--text-primary)', fontSize: '13px', outline: 'none',
  },
  section: { marginBottom: '24px' },
  sectionTitle: { fontSize: '13px', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' },
  divider: { borderTop: '1px solid var(--border-color)', margin: '20px 0' },
  error: { padding: '10px 14px', background: '#ef444422', border: '1px solid #ef444466', borderRadius: '6px', color: '#fca5a5', fontSize: '13px', marginBottom: '12px' },
  success: { padding: '10px 14px', background: '#22c55e22', border: '1px solid #22c55e66', borderRadius: '6px', color: '#86efac', fontSize: '13px', marginBottom: '12px' },
};

const TYPE_COLORS = {
  decision_table: '#06b6d4',
  condition_action: '#8b5cf6',
  tree: '#f59e0b',
  scorecard: '#22c55e',
};

const TYPE_LABELS = {
  decision_table: 'Decision Table',
  condition_action: 'Condition / Action',
  tree: 'Decision Tree',
  scorecard: 'Scorecard',
};

const ACTION_TYPES = ['set_field', 'add_tag', 'remove_tag', 'validation_error', 'return_output', 'call_workflow'];
const OPERATORS = ['eq', 'ne', 'lt', 'lte', 'gt', 'gte', 'in', 'not_in', 'between', 'contains', 'startswith', 'endswith', 'is_null', 'is_not_null', 'regex', 'expr'];
const REVIEW_STATUS_COLORS = { draft: '#71717a', pending_approval: '#f59e0b', approved: '#22c55e', rejected: '#ef4444', published: '#06b6d4' };

// ---------------------------------------------------------------------------
// ConditionActionEditor
// ---------------------------------------------------------------------------
function ConditionActionEditor({ body, onChange }) {
  const rules = body?.rules || [];

  const updateRule = (i, patch) => {
    const next = rules.map((r, idx) => idx === i ? { ...r, ...patch } : r);
    onChange({ ...body, rules: next });
  };

  const addRule = () => onChange({ ...body, rules: [...rules, { name: 'New Rule', when: '', then: [], priority: 0, enabled: true }] });
  const removeRule = (i) => onChange({ ...body, rules: rules.filter((_, idx) => idx !== i) });

  const addAction = (ruleIdx) => {
    const rule = rules[ruleIdx];
    const then = [...(rule.then || []), { type: 'set_field', path: '', value: '' }];
    updateRule(ruleIdx, { then });
  };

  const updateAction = (ruleIdx, actionIdx, patch) => {
    const rule = rules[ruleIdx];
    const then = (rule.then || []).map((a, i) => i === actionIdx ? { ...a, ...patch } : a);
    updateRule(ruleIdx, { then });
  };

  const removeAction = (ruleIdx, actionIdx) => {
    const rule = rules[ruleIdx];
    updateRule(ruleIdx, { then: (rule.then || []).filter((_, i) => i !== actionIdx) });
  };

  return (
    <div>
      {rules.map((rule, i) => (
        <div key={i} style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '14px', marginBottom: '10px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
            <input value={rule.name} onChange={e => updateRule(i, { name: e.target.value })} style={{ ...S.input, fontWeight: 600, flex: 1 }} placeholder="Rule name" />
            <input type="number" value={rule.priority || 0} onChange={e => updateRule(i, { priority: Number(e.target.value) })} style={{ ...S.input, width: '70px' }} title="Priority (lower = first)" />
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={rule.enabled !== false} onChange={e => updateRule(i, { enabled: e.target.checked })} />
              Enabled
            </label>
            <button onClick={() => removeRule(i)} style={S.btn('danger')}>✕</button>
          </div>
          <label style={S.label}>WHEN (Jinja2 / leave blank = always fires)</label>
          <textarea value={rule.when || ''} onChange={e => updateRule(i, { when: e.target.value })} style={{ ...S.textarea, minHeight: '48px', marginBottom: '10px' }} placeholder="case.priority == 'critical' and case.status == 'open'" />
          <label style={S.label}>THEN — Actions</label>
          {(rule.then || []).map((action, ai) => (
            <ActionRow key={ai} action={action} onChange={p => updateAction(i, ai, p)} onRemove={() => removeAction(i, ai)} />
          ))}
          <button onClick={() => addAction(i)} style={{ ...S.btn('ghost'), fontSize: '12px', marginTop: '6px' }}>+ Add Action</button>
        </div>
      ))}
      <button onClick={addRule} style={S.btn('ghost')}>+ Add Rule</button>
    </div>
  );
}

function ActionRow({ action, onChange, onRemove }) {
  const t = action.type || 'set_field';
  return (
    <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-start', marginBottom: '6px', padding: '8px', background: 'var(--bg-secondary)', borderRadius: '6px' }}>
      <select value={t} onChange={e => onChange({ type: e.target.value })} style={{ ...S.select, minWidth: '140px', fontSize: '12px' }}>
        {ACTION_TYPES.map(at => <option key={at} value={at}>{at}</option>)}
      </select>
      {t === 'set_field' && <>
        <input value={action.path || ''} onChange={e => onChange({ ...action, path: e.target.value })} style={{ ...S.input, flex: 1, fontSize: '12px' }} placeholder="path e.g. case.priority" />
        <input value={action.value || ''} onChange={e => onChange({ ...action, value: e.target.value })} style={{ ...S.input, flex: 1, fontSize: '12px' }} placeholder="value" />
      </>}
      {(t === 'add_tag' || t === 'remove_tag') && (
        <input value={action.tag || ''} onChange={e => onChange({ ...action, tag: e.target.value })} style={{ ...S.input, flex: 1, fontSize: '12px' }} placeholder="tag name" />
      )}
      {t === 'validation_error' && <>
        <input value={action.field || ''} onChange={e => onChange({ ...action, field: e.target.value })} style={{ ...S.input, flex: 1, fontSize: '12px' }} placeholder="field name" />
        <input value={action.message || ''} onChange={e => onChange({ ...action, message: e.target.value })} style={{ ...S.input, flex: 1, fontSize: '12px' }} placeholder="error message" />
      </>}
      {t === 'return_output' && (
        <textarea value={JSON.stringify(action.outputs || {}, null, 2)} onChange={e => { try { onChange({ ...action, outputs: JSON.parse(e.target.value) }); } catch { } }} style={{ ...S.textarea, flex: 1, fontSize: '12px', minHeight: '48px' }} placeholder='{"decision": "approve"}' />
      )}
      {t === 'call_workflow' && (
        <input value={action.workflow_id || ''} onChange={e => onChange({ ...action, workflow_id: e.target.value })} style={{ ...S.input, flex: 1, fontSize: '12px' }} placeholder="Workflow UUID" />
      )}
      <button onClick={onRemove} style={{ ...S.btn('danger'), padding: '4px 10px', flexShrink: 0 }}>✕</button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DecisionTableEditor
// ---------------------------------------------------------------------------
function DecisionTableEditor({ body, onChange }) {
  const inputCols = body?.input_columns || [];
  const outputCols = body?.output_columns || [];
  const rules = body?.rules || [];
  const matchMode = body?.match_mode || 'first';

  const update = (patch) => onChange({ ...body, ...patch });
  const addInputCol = () => update({ input_columns: [...inputCols, { key: `input_${inputCols.length + 1}`, label: `Input ${inputCols.length + 1}` }] });
  const addOutputCol = () => update({ output_columns: [...outputCols, { key: `output_${outputCols.length + 1}`, label: `Output ${outputCols.length + 1}`, type: 'value' }] });
  const addRule = () => {
    const rowInputs = Object.fromEntries(inputCols.map(c => [c.key, { op: 'eq', value: '' }]));
    const rowOutputs = Object.fromEntries(outputCols.map(c => [c.key, '']));
    update({ rules: [...rules, { inputs: rowInputs, outputs: rowOutputs, is_default: false }] });
  };
  const removeRule = (i) => update({ rules: rules.filter((_, idx) => idx !== i) });

  const updateCellInput = (ruleIdx, colKey, patch) => {
    const next = rules.map((r, i) => {
      if (i !== ruleIdx) return r;
      const cell = typeof r.inputs[colKey] === 'object' ? r.inputs[colKey] : { op: 'eq', value: r.inputs[colKey] };
      return { ...r, inputs: { ...r.inputs, [colKey]: { ...cell, ...patch } } };
    });
    update({ rules: next });
  };

  const updateCellOutput = (ruleIdx, colKey, value) => {
    const next = rules.map((r, i) => i === ruleIdx ? { ...r, outputs: { ...r.outputs, [colKey]: value } } : r);
    update({ rules: next });
  };

  const thStyle = { padding: '8px 10px', fontSize: '11px', fontWeight: 700, color: 'var(--text-secondary)', textAlign: 'left', borderBottom: '2px solid var(--border-color)', whiteSpace: 'nowrap', background: 'var(--bg-primary)' };
  const tdStyle = { padding: '4px 6px', borderBottom: '1px solid var(--border-color)', verticalAlign: 'top' };

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', alignItems: 'center' }}>
        <label style={S.label}>Match Mode</label>
        <select value={matchMode} onChange={e => update({ match_mode: e.target.value })} style={S.select}>
          {['first', 'all', 'unique', 'priority', 'collect'].map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <button onClick={addInputCol} style={S.btn('ghost')}>+ Input Column</button>
        <button onClick={addOutputCol} style={S.btn('ghost')}>+ Output Column</button>
        <button onClick={addRule} style={S.btn('ghost')}>+ Row</button>
      </div>

      {/* Column editors */}
      {(inputCols.length > 0 || outputCols.length > 0) && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
          {inputCols.map((col, ci) => (
            <div key={ci} style={{ display: 'flex', gap: '4px', alignItems: 'center', padding: '4px 8px', background: '#06b6d422', borderRadius: '6px', border: '1px solid #06b6d455' }}>
              <span style={{ fontSize: '11px', color: '#67e8f9' }}>IN</span>
              <input value={col.label} onChange={e => { const c = [...inputCols]; c[ci] = { ...col, label: e.target.value }; update({ input_columns: c }); }} style={{ ...S.input, width: '90px', fontSize: '11px', padding: '3px 6px' }} />
              <input value={col.key} onChange={e => { const c = [...inputCols]; c[ci] = { ...col, key: e.target.value }; update({ input_columns: c }); }} style={{ ...S.input, width: '80px', fontSize: '11px', padding: '3px 6px' }} placeholder="key" />
              <button onClick={() => update({ input_columns: inputCols.filter((_, i) => i !== ci) })} style={{ fontSize: '11px', background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer' }}>✕</button>
            </div>
          ))}
          {outputCols.map((col, ci) => (
            <div key={ci} style={{ display: 'flex', gap: '4px', alignItems: 'center', padding: '4px 8px', background: '#22c55e22', borderRadius: '6px', border: '1px solid #22c55e55' }}>
              <span style={{ fontSize: '11px', color: '#86efac' }}>OUT</span>
              <input value={col.label} onChange={e => { const c = [...outputCols]; c[ci] = { ...col, label: e.target.value }; update({ output_columns: c }); }} style={{ ...S.input, width: '90px', fontSize: '11px', padding: '3px 6px' }} />
              <input value={col.key} onChange={e => { const c = [...outputCols]; c[ci] = { ...col, key: e.target.value }; update({ output_columns: c }); }} style={{ ...S.input, width: '80px', fontSize: '11px', padding: '3px 6px' }} placeholder="key" />
              <button onClick={() => update({ output_columns: outputCols.filter((_, i) => i !== ci) })} style={{ fontSize: '11px', background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer' }}>✕</button>
            </div>
          ))}
        </div>
      )}

      {/* Grid */}
      {rules.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '12px' }}>
            <thead>
              <tr>
                <th style={thStyle}>#</th>
                {inputCols.map(c => <th key={c.key} style={{ ...thStyle, color: '#67e8f9' }}>{c.label}</th>)}
                {outputCols.map(c => <th key={c.key} style={{ ...thStyle, color: '#86efac' }}>{c.label}</th>)}
                <th style={thStyle}>Default</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule, ri) => (
                <tr key={ri}>
                  <td style={{ ...tdStyle, color: 'var(--text-secondary)', fontSize: '11px' }}>{ri + 1}</td>
                  {inputCols.map(col => {
                    const cell = rule.inputs?.[col.key];
                    const op = typeof cell === 'object' ? cell.op || 'eq' : 'eq';
                    const val = typeof cell === 'object' ? (cell.value ?? '') : (cell ?? '');
                    return (
                      <td key={col.key} style={tdStyle}>
                        <div style={{ display: 'flex', gap: '3px' }}>
                          <select value={op} onChange={e => updateCellInput(ri, col.key, { op: e.target.value })} style={{ ...S.select, fontSize: '11px', padding: '3px 4px', maxWidth: '90px' }}>
                            {OPERATORS.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                          {op !== 'is_null' && op !== 'is_not_null' && (
                            <input value={String(val)} onChange={e => updateCellInput(ri, col.key, { value: e.target.value })} style={{ ...S.input, fontSize: '11px', padding: '3px 6px', minWidth: '60px' }} placeholder="value" />
                          )}
                        </div>
                      </td>
                    );
                  })}
                  {outputCols.map(col => (
                    <td key={col.key} style={tdStyle}>
                      <input value={String(rule.outputs?.[col.key] ?? '')} onChange={e => updateCellOutput(ri, col.key, e.target.value)} style={{ ...S.input, fontSize: '11px', padding: '3px 6px', minWidth: '80px' }} />
                    </td>
                  ))}
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <input type="checkbox" checked={!!rule.is_default} onChange={e => {
                      const next = rules.map((r, i) => ({ ...r, is_default: i === ri ? e.target.checked : (e.target.checked ? false : r.is_default) }));
                      update({ rules: next });
                    }} />
                  </td>
                  <td style={tdStyle}>
                    <button onClick={() => removeRule(ri)} style={{ ...S.btn('danger'), padding: '2px 8px', fontSize: '11px' }}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TreeEditor — recursive binary decision tree (no ReactFlow dependency)
// ---------------------------------------------------------------------------
function TreeNodeRow({ nodeId, parentId, branch, nodes, depth, onUpdate, onAdd, onRemove, onConvertToLeaf, onConvertToCondition }) {
  const node = nodes[nodeId];

  // Missing node = open branch slot → show "add" buttons
  if (!node) {
    return (
      <div style={{ marginLeft: `${depth * 32}px`, display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
        {branch && (
          <span style={{ fontSize: '10px', fontWeight: 700, color: branch === 'yes' ? '#22c55e' : '#ef4444', minWidth: '32px' }}>
            {branch === 'yes' ? 'YES' : 'NO '}
          </span>
        )}
        <button onClick={() => onAdd(parentId, branch, 'condition')} style={{ ...S.btn('ghost'), fontSize: '11px', padding: '3px 10px' }}>+ Condition</button>
        <button onClick={() => onAdd(parentId, branch, 'leaf')} style={{ ...S.btn('ghost'), fontSize: '11px', padding: '3px 10px' }}>+ Leaf</button>
      </div>
    );
  }

  const isCondition = node.type === 'condition';
  const borderColor = isCondition ? '#06b6d4' : '#22c55e';
  const bg = isCondition ? 'rgba(6,182,212,0.06)' : 'rgba(34,197,94,0.06)';
  const branchColor = branch === 'yes' ? '#22c55e' : branch === 'no' ? '#ef4444' : 'transparent';

  return (
    <div style={{ marginLeft: `${depth * 32}px` }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '6px' }}>
        {branch && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: '40px', paddingTop: '10px', flexShrink: 0 }}>
            <div style={{ width: '16px', height: '1px', background: branchColor }} />
            <span style={{ fontSize: '10px', fontWeight: 700, color: branchColor }}>
              {branch === 'yes' ? 'YES' : 'NO'}
            </span>
          </div>
        )}
        <div style={{ flex: 1, border: `1px solid ${borderColor}44`, borderRadius: '6px', padding: '8px 12px', background: bg }}>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: isCondition ? '8px' : 0 }}>
            <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 6px', borderRadius: '4px', background: `${borderColor}22`, color: borderColor }}>
              {isCondition ? 'IF' : 'LEAF'}
            </span>
            {isCondition ? (
              <button onClick={() => onConvertToLeaf(nodeId)} style={{ ...S.btn('ghost'), fontSize: '10px', padding: '1px 6px' }}>→ Leaf</button>
            ) : depth > 0 ? (
              <button onClick={() => onConvertToCondition(nodeId)} style={{ ...S.btn('ghost'), fontSize: '10px', padding: '1px 6px' }}>→ Condition</button>
            ) : null}
            <div style={{ flex: 1 }} />
            {parentId !== '__root__' && (
              <button onClick={() => onRemove(nodeId, parentId, branch)} style={{ ...S.btn('danger'), fontSize: '10px', padding: '1px 8px' }}>✕</button>
            )}
          </div>

          {isCondition && (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                value={node.field || ''} onChange={e => onUpdate(nodeId, { field: e.target.value })}
                style={{ ...S.input, flex: 1, minWidth: '120px', fontSize: '12px', padding: '4px 8px' }}
                placeholder="field (e.g. case.priority)"
              />
              <select value={node.op || 'eq'} onChange={e => onUpdate(nodeId, { op: e.target.value })} style={{ ...S.select, fontSize: '12px', padding: '4px 6px' }}>
                {OPERATORS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
              {!['is_null', 'is_not_null'].includes(node.op || 'eq') && (
                <input
                  value={node.value !== undefined && node.value !== null ? String(node.value) : ''}
                  onChange={e => onUpdate(nodeId, { value: e.target.value })}
                  style={{ ...S.input, flex: 1, minWidth: '80px', fontSize: '12px', padding: '4px 8px' }}
                  placeholder="value"
                />
              )}
            </div>
          )}

          {!isCondition && (
            <div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Output values:</div>
              {Object.entries(node.outputs || {}).map(([k, v], i) => (
                <div key={i} style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                  <input value={k} onChange={e => {
                    const out = { ...(node.outputs || {}) };
                    const newOut = {}; for (const [ok, ov] of Object.entries(out)) { newOut[ok === k ? e.target.value : ok] = ov; }
                    onUpdate(nodeId, { outputs: newOut });
                  }} style={{ ...S.input, flex: 1, fontSize: '12px', padding: '3px 6px' }} placeholder="key" />
                  <input value={String(v)} onChange={e => onUpdate(nodeId, { outputs: { ...(node.outputs || {}), [k]: e.target.value } })} style={{ ...S.input, flex: 1, fontSize: '12px', padding: '3px 6px' }} placeholder="value" />
                  <button onClick={() => { const out = { ...(node.outputs || {}) }; delete out[k]; onUpdate(nodeId, { outputs: out }); }} style={{ ...S.btn('danger'), padding: '2px 8px', fontSize: '11px' }}>✕</button>
                </div>
              ))}
              <button onClick={() => {
                const idx = Object.keys(node.outputs || {}).length + 1;
                onUpdate(nodeId, { outputs: { ...(node.outputs || {}), [`output_${idx}`]: '' } });
              }} style={{ ...S.btn('ghost'), fontSize: '11px', padding: '3px 8px' }}>+ Output</button>
            </div>
          )}
        </div>
      </div>

      {isCondition && (
        <div style={{ marginLeft: branch ? '48px' : '0' }}>
          <TreeNodeRow nodeId={node.yes_id} parentId={nodeId} branch="yes" nodes={nodes} depth={depth + 1}
            onUpdate={onUpdate} onAdd={onAdd} onRemove={onRemove} onConvertToLeaf={onConvertToLeaf} onConvertToCondition={onConvertToCondition} />
          <TreeNodeRow nodeId={node.no_id} parentId={nodeId} branch="no" nodes={nodes} depth={depth + 1}
            onUpdate={onUpdate} onAdd={onAdd} onRemove={onRemove} onConvertToLeaf={onConvertToLeaf} onConvertToCondition={onConvertToCondition} />
        </div>
      )}
    </div>
  );
}

function TreeEditor({ body, onChange }) {
  const nodes = body?.nodes || {};
  const rootId = body?.root_id;

  const genId = () => 'n_' + Math.random().toString(36).slice(2, 8);

  const update = (newNodes, newRootId) => onChange({ ...body, nodes: newNodes, root_id: newRootId !== undefined ? newRootId : rootId });

  const addNode = (parentId, branch, type) => {
    const id = genId();
    const newNode = type === 'leaf'
      ? { id, type: 'leaf', outputs: { decision: '' } }
      : { id, type: 'condition', field: '', op: 'eq', value: '', yes_id: null, no_id: null };
    const newNodes = { ...nodes, [id]: newNode };
    if (parentId === '__root__') {
      update(newNodes, id);
    } else {
      const key = branch === 'yes' ? 'yes_id' : 'no_id';
      update({ ...newNodes, [parentId]: { ...newNodes[parentId], [key]: id } });
    }
  };

  const updateNode = (id, patch) => update({ ...nodes, [id]: { ...nodes[id], ...patch } });

  const removeSubtree = (id, fromParentId, branch) => {
    const toRemove = new Set();
    const collect = (nid) => {
      if (!nid || !nodes[nid] || toRemove.has(nid)) return;
      toRemove.add(nid);
      if (nodes[nid].type === 'condition') { collect(nodes[nid].yes_id); collect(nodes[nid].no_id); }
    };
    collect(id);
    const newNodes = Object.fromEntries(Object.entries(nodes).filter(([k]) => !toRemove.has(k)));
    const key = branch === 'yes' ? 'yes_id' : 'no_id';
    if (newNodes[fromParentId]) newNodes[fromParentId] = { ...newNodes[fromParentId], [key]: null };
    update(newNodes);
  };

  const convertToLeaf = (id) => updateNode(id, { type: 'leaf', outputs: {}, field: undefined, op: undefined, value: undefined, yes_id: undefined, no_id: undefined });
  const convertToCondition = (id) => updateNode(id, { type: 'condition', field: '', op: 'eq', value: '', yes_id: null, no_id: null, outputs: undefined });

  if (!rootId || !nodes[rootId]) {
    return (
      <div>
        <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '12px' }}>Tree is empty. Add a root node to start.</div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => addNode('__root__', null, 'condition')} style={S.btn('ghost')}>+ Root Condition</button>
          <button onClick={() => addNode('__root__', null, 'leaf')} style={S.btn('ghost')}>+ Root Leaf</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <TreeNodeRow nodeId={rootId} parentId="__root__" branch={null} nodes={nodes} depth={0}
        onUpdate={updateNode} onAdd={addNode} onRemove={removeSubtree}
        onConvertToLeaf={convertToLeaf} onConvertToCondition={convertToCondition} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// ScorecardEditor
// ---------------------------------------------------------------------------
function ScorecardEditor({ body, onChange }) {
  const factors = body?.factors || [];
  const bands = body?.bands || [];
  const [previewInputs, setPreviewInputs] = useState({});
  const [previewScore, setPreviewScore] = useState(null);

  const update = (patch) => onChange({ ...body, ...patch });

  const updateFactor = (i, patch) => update({ factors: factors.map((f, idx) => idx === i ? { ...f, ...patch } : f) });
  const addFactor = () => update({
    factors: [...factors, { key: `factor_${factors.length + 1}`, label: `Factor ${factors.length + 1}`, weight: 1, mapping_type: 'linear', linear: { input_min: 0, input_max: 100, output_min: 0, output_max: 100 }, banded: [], boolean: { true_points: 100, false_points: 0 } }],
  });
  const removeFactor = (i) => update({ factors: factors.filter((_, idx) => idx !== i) });

  const updateBand = (i, patch) => update({ bands: bands.map((b, idx) => idx === i ? { ...b, ...patch } : b) });
  const addBand = () => update({ bands: [...bands, { min_score: 0, decision: '' }] });
  const removeBand = (i) => update({ bands: bands.filter((_, idx) => idx !== i) });

  const computePreview = () => {
    const totalWeight = factors.reduce((s, f) => s + Number(f.weight || 1), 0) || 1;
    let score = 0;
    for (const f of factors) {
      const weight = Number(f.weight || 1);
      const raw = Number(previewInputs[f.key] || 0);
      let points = 0;
      if (f.mapping_type === 'linear') {
        const l = f.linear || {};
        const inMin = Number(l.input_min || 0), inMax = Number(l.input_max || 100);
        const outMin = Number(l.output_min || 0), outMax = Number(l.output_max || 100);
        if (inMax !== inMin) {
          const clamped = Math.max(inMin, Math.min(inMax, raw));
          points = outMin + (clamped - inMin) / (inMax - inMin) * (outMax - outMin);
        }
      } else if (f.mapping_type === 'banded') {
        for (const band of (f.banded || [])) {
          if (band.threshold === null || band.threshold === undefined) { points = Number(band.points || 0); break; }
          const t = Number(band.threshold);
          const hit = (band.op === 'lt' && raw < t) || (band.op === 'lte' && raw <= t) ||
                      (band.op === 'gt' && raw > t) || (band.op === 'gte' && raw >= t);
          if (hit) { points = Number(band.points || 0); break; }
        }
      } else if (f.mapping_type === 'boolean') {
        const bm = f.boolean || {};
        points = previewInputs[f.key] ? Number(bm.true_points || 100) : Number(bm.false_points || 0);
      }
      score += (weight / totalWeight) * points;
    }
    const sorted = [...bands].sort((a, b) => Number(b.min_score || 0) - Number(a.min_score || 0));
    let decision = null;
    for (const band of sorted) { if (score >= Number(band.min_score || 0)) { decision = band.decision; break; } }
    setPreviewScore({ score: Math.round(score * 100) / 100, decision });
  };

  return (
    <div>
      <div style={S.sectionTitle}>Scoring Factors</div>
      {factors.map((f, fi) => (
        <div key={fi} style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '14px', marginBottom: '10px' }}>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'center' }}>
            <input value={f.label || ''} onChange={e => updateFactor(fi, { label: e.target.value })} style={{ ...S.input, flex: 1, fontWeight: 600 }} placeholder="Factor label" />
            <input value={f.key || ''} onChange={e => updateFactor(fi, { key: e.target.value })} style={{ ...S.input, width: '130px', fontSize: '12px' }} placeholder="field.key" />
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>wt</span>
              <input type="number" value={f.weight ?? 1} onChange={e => updateFactor(fi, { weight: Number(e.target.value) })} style={{ ...S.input, width: '60px', fontSize: '12px' }} />
            </div>
            <select value={f.mapping_type || 'linear'} onChange={e => updateFactor(fi, { mapping_type: e.target.value })} style={S.select}>
              <option value="linear">Linear</option>
              <option value="banded">Banded</option>
              <option value="boolean">Boolean</option>
            </select>
            <button onClick={() => removeFactor(fi)} style={S.btn('danger')}>✕</button>
          </div>

          {f.mapping_type === 'linear' && (
            <div style={{ display: 'flex', gap: '10px', fontSize: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--text-secondary)' }}>Input range:</span>
              <input type="number" value={f.linear?.input_min ?? 0} onChange={e => updateFactor(fi, { linear: { ...(f.linear || {}), input_min: Number(e.target.value) } })} style={{ ...S.input, width: '70px', fontSize: '12px' }} placeholder="min" />
              <span>→</span>
              <input type="number" value={f.linear?.input_max ?? 100} onChange={e => updateFactor(fi, { linear: { ...(f.linear || {}), input_max: Number(e.target.value) } })} style={{ ...S.input, width: '70px', fontSize: '12px' }} placeholder="max" />
              <span style={{ color: 'var(--text-secondary)', marginLeft: '12px' }}>Points:</span>
              <input type="number" value={f.linear?.output_min ?? 0} onChange={e => updateFactor(fi, { linear: { ...(f.linear || {}), output_min: Number(e.target.value) } })} style={{ ...S.input, width: '60px', fontSize: '12px' }} placeholder="0" />
              <span>→</span>
              <input type="number" value={f.linear?.output_max ?? 100} onChange={e => updateFactor(fi, { linear: { ...(f.linear || {}), output_max: Number(e.target.value) } })} style={{ ...S.input, width: '60px', fontSize: '12px' }} placeholder="100" />
            </div>
          )}

          {f.mapping_type === 'banded' && (
            <div>
              {(f.banded || []).map((band, bi) => (
                <div key={bi} style={{ display: 'flex', gap: '6px', marginBottom: '4px', alignItems: 'center' }}>
                  <select value={band.op || 'lt'} onChange={e => { const b = [...(f.banded || [])]; b[bi] = { ...b[bi], op: e.target.value }; updateFactor(fi, { banded: b }); }} style={{ ...S.select, fontSize: '12px', padding: '3px 6px' }}>
                    {['lt', 'lte', 'gt', 'gte'].map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                  <input type="number" value={band.threshold ?? ''} onChange={e => { const b = [...(f.banded || [])]; b[bi] = { ...b[bi], threshold: e.target.value === '' ? null : Number(e.target.value) }; updateFactor(fi, { banded: b }); }} style={{ ...S.input, width: '100px', fontSize: '12px', padding: '3px 6px' }} placeholder="threshold (blank=else)" />
                  <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>→</span>
                  <input type="number" value={band.points ?? 0} onChange={e => { const b = [...(f.banded || [])]; b[bi] = { ...b[bi], points: Number(e.target.value) }; updateFactor(fi, { banded: b }); }} style={{ ...S.input, width: '70px', fontSize: '12px', padding: '3px 6px' }} placeholder="pts" />
                  <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>pts</span>
                  <button onClick={() => updateFactor(fi, { banded: (f.banded || []).filter((_, i) => i !== bi) })} style={{ fontSize: '11px', background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer' }}>✕</button>
                </div>
              ))}
              <button onClick={() => updateFactor(fi, { banded: [...(f.banded || []), { op: 'lt', threshold: 0, points: 0 }] })} style={{ ...S.btn('ghost'), fontSize: '12px', marginTop: '4px' }}>+ Band</button>
            </div>
          )}

          {f.mapping_type === 'boolean' && (
            <div style={{ display: 'flex', gap: '16px', fontSize: '12px', alignItems: 'center' }}>
              <span style={{ color: 'var(--text-secondary)' }}>true =</span>
              <input type="number" value={f.boolean?.true_points ?? 100} onChange={e => updateFactor(fi, { boolean: { ...(f.boolean || {}), true_points: Number(e.target.value) } })} style={{ ...S.input, width: '70px', fontSize: '12px' }} />
              <span style={{ color: 'var(--text-secondary)' }}>pts, false =</span>
              <input type="number" value={f.boolean?.false_points ?? 0} onChange={e => updateFactor(fi, { boolean: { ...(f.boolean || {}), false_points: Number(e.target.value) } })} style={{ ...S.input, width: '70px', fontSize: '12px' }} />
              <span style={{ color: 'var(--text-secondary)' }}>pts</span>
            </div>
          )}
        </div>
      ))}
      <button onClick={addFactor} style={{ ...S.btn('ghost'), marginBottom: '20px' }}>+ Add Factor</button>

      <div style={S.sectionTitle}>Score Bands → Decision</div>
      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>First band where total score ≥ min_score wins. List highest first.</div>
      {bands.map((band, bi) => (
        <div key={bi} style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'center' }}>
          <span style={{ color: 'var(--text-secondary)', fontSize: '12px', minWidth: '60px' }}>score ≥</span>
          <input type="number" value={band.min_score ?? 0} onChange={e => updateBand(bi, { min_score: Number(e.target.value) })} style={{ ...S.input, width: '80px', fontSize: '12px' }} />
          <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>→</span>
          <input value={band.decision || ''} onChange={e => updateBand(bi, { decision: e.target.value })} style={{ ...S.input, flex: 1, fontSize: '12px' }} placeholder="decision label (e.g. auto_approve)" />
          <button onClick={() => removeBand(bi)} style={{ ...S.btn('danger'), padding: '4px 10px' }}>✕</button>
        </div>
      ))}
      <button onClick={addBand} style={{ ...S.btn('ghost'), marginBottom: '20px' }}>+ Add Band</button>

      <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '14px' }}>
        <div style={S.sectionTitle}>Live Score Preview</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
          {factors.map(f => (
            <div key={f.key} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{f.label || f.key}:</label>
              {f.mapping_type === 'boolean'
                ? <input type="checkbox" checked={!!previewInputs[f.key]} onChange={e => setPreviewInputs(p => ({ ...p, [f.key]: e.target.checked }))} />
                : <input type="number" value={previewInputs[f.key] ?? ''} onChange={e => setPreviewInputs(p => ({ ...p, [f.key]: e.target.value }))} style={{ ...S.input, width: '90px', fontSize: '12px', padding: '4px 6px' }} />
              }
            </div>
          ))}
        </div>
        <button onClick={computePreview} style={{ ...S.btn('ghost'), fontSize: '12px' }}>Compute</button>
        {previewScore && (
          <div style={{ marginTop: '10px', display: 'flex', gap: '12px', alignItems: 'center' }}>
            <span style={{ fontSize: '28px', fontWeight: 700, color: 'var(--accent-color)' }}>{previewScore.score}</span>
            <span style={{ fontSize: '16px', color: 'var(--text-secondary)' }}>→</span>
            <span style={S.badge('#22c55e')}>{previewScore.decision || '—'}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EvaluatePanel
// ---------------------------------------------------------------------------
function EvaluatePanel({ workspaceId, artifactId, ruleSetId }) {
  const [inputText, setInputText] = useState('{}');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const run = async () => {
    setErr(''); setResult(null); setLoading(true);
    try {
      let input = {};
      try { input = JSON.parse(inputText); } catch { throw new Error('Input JSON is invalid'); }
      const path = ruleSetId
        ? `/api/rules/workspaces/${workspaceId}/sets/${ruleSetId}/evaluate`
        : `/api/rules/workspaces/${workspaceId}/artifacts/${artifactId}/evaluate`;
      const res = await apiFetch(path, { method: 'POST', body: JSON.stringify({ input, trace: false }) });
      setResult(res);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };

  return (
    <div style={{ marginTop: '20px', padding: '16px', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
      <div style={S.sectionTitle}>⚡ Test / Evaluate</div>
      <label style={S.label}>Input JSON</label>
      <textarea value={inputText} onChange={e => setInputText(e.target.value)} style={{ ...S.textarea, minHeight: '80px', marginBottom: '10px' }} />
      {err && <div style={S.error}>{err}</div>}
      <button onClick={run} disabled={loading} style={S.btn('primary')}>{loading ? 'Running…' : 'Run'}</button>
      {result && (
        <div style={{ marginTop: '14px' }}>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            <span style={S.badge(result.matched ? '#22c55e' : '#71717a')}>{result.matched ? 'MATCHED' : 'NO MATCH'}</span>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{result.duration_ms}ms</span>
          </div>
          {Object.keys(result.outputs || {}).length > 0 && (
            <>
              <div style={{ ...S.label, marginBottom: '6px' }}>Outputs</div>
              <pre style={{ background: 'var(--bg-secondary)', padding: '10px', borderRadius: '6px', fontSize: '12px', overflow: 'auto', margin: 0 }}>{JSON.stringify(result.outputs, null, 2)}</pre>
            </>
          )}
          {result.validation_errors?.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              <div style={{ ...S.label, marginBottom: '6px' }}>Validation Errors</div>
              {result.validation_errors.map((e, i) => (
                <div key={i} style={S.error}>{e.field}: {e.message}</div>
              ))}
            </div>
          )}
          {result.errors?.length > 0 && (
            <div style={S.error}>{result.errors.join('; ')}</div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ArtifactDetail (editor + versions)
// ---------------------------------------------------------------------------
function ArtifactDetail({ workspaceId, artifact, onBack, onSaved }) {
  const [name, setName] = useState(artifact?.name || '');
  const [description, setDescription] = useState(artifact?.description || '');
  const [body, setBody] = useState({});
  const [versions, setVersions] = useState([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const isNew = !artifact;
  const [type, setType] = useState(artifact?.type || 'condition_action');

  const defaultBody = useCallback((t) => {
    if (t === 'condition_action') return { rules: [] };
    if (t === 'decision_table') return { input_columns: [], output_columns: [], rules: [], match_mode: 'first' };
    if (t === 'tree') return { root_id: null, nodes: {} };
    if (t === 'scorecard') return { factors: [], bands: [] };
    return {};
  }, []);

  useEffect(() => {
    if (!artifact) { setBody(defaultBody(type)); return; }
    setBody(defaultBody(artifact.type));
    if (!artifact.current_version_id) return;
    apiFetch(`/api/rules/workspaces/${workspaceId}/artifacts/${artifact.id}/versions`)
      .then(vs => {
        setVersions(vs);
        const published = vs.find(v => v.id === artifact.current_version_id);
        if (published) setBody(published.body || defaultBody(artifact.type));
      })
      .catch(() => {});
  }, [artifact, workspaceId, defaultBody, type]);

  const save = async () => {
    setErr(''); setSaving(true);
    try {
      if (isNew) {
        await apiFetch(`/api/rules/workspaces/${workspaceId}/artifacts`, {
          method: 'POST',
          body: JSON.stringify({ name, description, type, body }),
        });
        setMsg('Created!');
        onSaved();
      } else {
        await apiFetch(`/api/rules/workspaces/${workspaceId}/artifacts/${artifact.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ name, description }),
        });
        // Create + publish new version
        const v = await apiFetch(`/api/rules/workspaces/${workspaceId}/artifacts/${artifact.id}/versions`, {
          method: 'POST',
          body: JSON.stringify({ body, notes }),
        });
        await apiFetch(`/api/rules/workspaces/${workspaceId}/artifacts/${artifact.id}/versions/${v.id}/publish`, { method: 'POST' });
        setMsg('Saved & published!');
        setVersions(prev => [v, ...prev]);
        onSaved();
      }
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };

  return (
    <div>
      <div style={S.row}>
        <button onClick={onBack} style={S.btn('ghost')}>← Back</button>
        <h2 style={{ margin: 0, fontSize: '16px' }}>{isNew ? 'New Rule Artifact' : artifact.name}</h2>
        {!isNew && <span style={S.badge(TYPE_COLORS[artifact.type])}>{TYPE_LABELS[artifact.type]}</span>}
      </div>

      {err && <div style={S.error}>{err}</div>}
      {msg && <div style={S.success}>{msg}</div>}

      <div style={S.section}>
        <label style={S.label}>Name</label>
        <input value={name} onChange={e => setName(e.target.value)} style={{ ...S.input, marginBottom: '10px' }} />
        <label style={S.label}>Description</label>
        <input value={description} onChange={e => setDescription(e.target.value)} style={{ ...S.input, marginBottom: '10px' }} />
        {isNew && (
          <>
            <label style={S.label}>Type</label>
            <select value={type} onChange={e => { setType(e.target.value); setBody(defaultBody(e.target.value)); }} style={{ ...S.select, marginBottom: '10px' }}>
              <option value="condition_action">Condition / Action (When → Then rules)</option>
              <option value="decision_table">Decision Table (Spreadsheet if-then)</option>
              <option value="tree">Decision Tree (Binary tree with condition nodes)</option>
              <option value="scorecard">Scorecard (Weighted-sum scoring + bands)</option>
            </select>
          </>
        )}
      </div>

      <div style={S.divider} />
      <div style={S.sectionTitle}>Rule Body</div>

      {(isNew ? type : artifact?.type) === 'condition_action' && (
        <ConditionActionEditor body={body} onChange={setBody} />
      )}
      {(isNew ? type : artifact?.type) === 'decision_table' && (
        <DecisionTableEditor body={body} onChange={setBody} />
      )}
      {(isNew ? type : artifact?.type) === 'tree' && (
        <TreeEditor body={body} onChange={setBody} />
      )}
      {(isNew ? type : artifact?.type) === 'scorecard' && (
        <ScorecardEditor body={body} onChange={setBody} />
      )}

      {!isNew && (
        <>
          <label style={{ ...S.label, marginTop: '12px' }}>Version Notes</label>
          <input value={notes} onChange={e => setNotes(e.target.value)} style={{ ...S.input, marginBottom: '10px' }} placeholder="What changed in this version?" />
        </>
      )}

      <button onClick={save} disabled={saving} style={{ ...S.btn('primary'), marginTop: '8px' }}>
        {saving ? 'Saving…' : (isNew ? 'Create' : 'Save & Publish New Version')}
      </button>

      {!isNew && (
        <>
          <EvaluatePanel workspaceId={workspaceId} artifactId={artifact.id} />

          {versions.length > 0 && (
            <>
              <div style={{ ...S.divider, marginTop: '24px' }} />
              <div style={S.sectionTitle}>Version History</div>
              {versions.map(v => (
                <div key={v.id} style={{ ...S.card, flexDirection: 'column', cursor: 'default', gap: '6px' }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                    <span style={{ fontWeight: 600, fontSize: '13px' }}>v{v.version}</span>
                    <span style={S.badge(v.is_published ? '#22c55e' : '#71717a')}>{v.is_published ? 'Published' : 'Draft'}</span>
                    {v.review_status && v.review_status !== 'draft' && (
                      <span style={S.badge(REVIEW_STATUS_COLORS[v.review_status] || '#71717a')}>{v.review_status.replace('_', ' ')}</span>
                    )}
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)', flex: 1 }}>{v.notes || '—'}</span>
                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{v.created_at?.slice(0, 10)}</span>
                    <button onClick={() => setBody(v.body || {})} style={{ ...S.btn('ghost'), fontSize: '12px', padding: '4px 10px' }}>Load</button>
                    {!v.is_published && v.review_status === 'draft' && (
                      <button onClick={async () => {
                        try {
                          await apiFetch(`/api/rules/workspaces/${workspaceId}/artifacts/${artifact.id}/versions/${v.id}/submit-for-review`, { method: 'POST' });
                          setVersions(prev => prev.map(x => x.id === v.id ? { ...x, review_status: 'pending_approval' } : x));
                        } catch (e) { setErr(e.message); }
                      }} style={{ ...S.btn('ghost'), fontSize: '12px', padding: '4px 10px', color: '#f59e0b', borderColor: '#f59e0b55' }}>
                        Submit for Review
                      </button>
                    )}
                  </div>
                  {v.review_notes && <div style={{ fontSize: '12px', color: 'var(--text-secondary)', paddingLeft: '2px' }}>Reviewer: {v.review_notes}</div>}
                </div>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ArtifactsTab
// ---------------------------------------------------------------------------
function ArtifactsTab({ workspaceId }) {
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);  // null = list, 'new' = create, artifact = edit
  const [typeFilter, setTypeFilter] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    apiFetch(`/api/rules/workspaces/${workspaceId}/artifacts`)
      .then(data => setArtifacts(data))
      .catch(() => setArtifacts([]))
      .finally(() => setLoading(false));
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  if (selected === 'new') {
    return <ArtifactDetail workspaceId={workspaceId} artifact={null} onBack={() => setSelected(null)} onSaved={() => { setSelected(null); load(); }} />;
  }
  if (selected) {
    return <ArtifactDetail workspaceId={workspaceId} artifact={selected} onBack={() => setSelected(null)} onSaved={load} />;
  }

  const filtered = typeFilter ? artifacts.filter(a => a.type === typeFilter) : artifacts;

  return (
    <div>
      <div style={S.row}>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={S.select}>
          <option value="">All types</option>
          {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button onClick={() => setSelected('new')} style={S.btn('primary')}>+ New Artifact</button>
      </div>
      {loading ? <div style={{ color: 'var(--text-secondary)' }}>Loading…</div> : null}
      {!loading && filtered.length === 0 && (
        <div style={{ color: 'var(--text-secondary)', fontSize: '14px', padding: '40px 0', textAlign: 'center' }}>
          No rule artifacts yet.<br />
          <span style={{ fontSize: '12px' }}>Create a Decision Table or Condition/Action rule to get started.</span>
        </div>
      )}
      {filtered.map(a => (
        <div key={a.id} style={S.card} onClick={() => setSelected(a)}
          onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent-color)'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
        >
          <span style={{ fontSize: '20px' }}>
            {a.type === 'decision_table' ? '📋' : a.type === 'condition_action' ? '⚡' : a.type === 'tree' ? '🌳' : '📊'}
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: '14px' }}>{a.name}</div>
            {a.description && <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>{a.description}</div>}
          </div>
          <span style={S.badge(TYPE_COLORS[a.type] || '#71717a')}>{TYPE_LABELS[a.type] || a.type}</span>
          <span style={S.badge(a.is_active ? '#22c55e' : '#71717a')}>{a.is_active ? 'Active' : 'Inactive'}</span>
          {a.current_version_id && <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>has version</span>}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SetDetail
// ---------------------------------------------------------------------------
function SetDetail({ workspaceId, ruleSet, allArtifacts, onBack, onSaved }) {
  const [name, setName] = useState(ruleSet?.name || '');
  const [description, setDescription] = useState(ruleSet?.description || '');
  const [groups, setGroups] = useState([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const isNew = !ruleSet;

  const POLICIES = ['stop_on_error', 'stop_on_first_match', 're_evaluate_on_change'];

  useEffect(() => {
    if (!ruleSet?.current_version_id) { setGroups([{ name: 'Group 1', policy: '', rules: [] }]); return; }
    apiFetch(`/api/rules/workspaces/${workspaceId}/sets/${ruleSet.id}/versions`)
      .then(vs => {
        const published = vs.find(v => v.id === ruleSet.current_version_id);
        setGroups(published?.groups || [{ name: 'Group 1', policy: '', rules: [] }]);
      })
      .catch(() => setGroups([{ name: 'Group 1', policy: '', rules: [] }]));
  }, [ruleSet, workspaceId]);

  const addGroup = () => setGroups(g => [...g, { name: `Group ${g.length + 1}`, policy: '', rules: [] }]);
  const removeGroup = (i) => setGroups(g => g.filter((_, idx) => idx !== i));
  const updateGroup = (i, patch) => setGroups(g => g.map((gr, idx) => idx === i ? { ...gr, ...patch } : gr));
  const addRuleRef = (gi) => {
    updateGroup(gi, { rules: [...(groups[gi].rules || []), { artifact_id: '' }] });
  };
  const updateRuleRef = (gi, ri, patch) => {
    const newRules = (groups[gi].rules || []).map((r, idx) => idx === ri ? { ...r, ...patch } : r);
    updateGroup(gi, { rules: newRules });
  };
  const removeRuleRef = (gi, ri) => {
    updateGroup(gi, { rules: (groups[gi].rules || []).filter((_, idx) => idx !== ri) });
  };

  const save = async () => {
    setErr(''); setSaving(true);
    try {
      if (isNew) {
        await apiFetch(`/api/rules/workspaces/${workspaceId}/sets`, {
          method: 'POST',
          body: JSON.stringify({ name, description, groups }),
        });
        setMsg('Created!');
        onSaved();
      } else {
        await apiFetch(`/api/rules/workspaces/${workspaceId}/sets/${ruleSet.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ name, description }),
        });
        const v = await apiFetch(`/api/rules/workspaces/${workspaceId}/sets/${ruleSet.id}/versions`, {
          method: 'POST',
          body: JSON.stringify({ groups, notes }),
        });
        await apiFetch(`/api/rules/workspaces/${workspaceId}/sets/${ruleSet.id}/versions/${v.id}/publish`, { method: 'POST' });
        setMsg('Saved & published!');
        onSaved();
      }
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };

  return (
    <div>
      <div style={S.row}>
        <button onClick={onBack} style={S.btn('ghost')}>← Back</button>
        <h2 style={{ margin: 0, fontSize: '16px' }}>{isNew ? 'New Rule Set' : ruleSet.name}</h2>
      </div>
      {err && <div style={S.error}>{err}</div>}
      {msg && <div style={S.success}>{msg}</div>}

      <label style={S.label}>Name</label>
      <input value={name} onChange={e => setName(e.target.value)} style={{ ...S.input, marginBottom: '10px' }} />
      <label style={S.label}>Description</label>
      <input value={description} onChange={e => setDescription(e.target.value)} style={{ ...S.input, marginBottom: '16px' }} />

      <div style={S.sectionTitle}>Groups</div>
      {groups.map((group, gi) => (
        <div key={gi} style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '14px', marginBottom: '10px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
            <input value={group.name} onChange={e => updateGroup(gi, { name: e.target.value })} style={{ ...S.input, fontWeight: 600, flex: 1 }} placeholder="Group name" />
            <select value={group.policy || ''} onChange={e => updateGroup(gi, { policy: e.target.value })} style={S.select}>
              <option value="">No special policy</option>
              {POLICIES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <button onClick={() => removeGroup(gi)} style={S.btn('danger')}>✕ Remove</button>
          </div>
          <label style={S.label}>Rules in this group</label>
          {(group.rules || []).map((ref, ri) => (
            <div key={ri} style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
              <select value={ref.artifact_id || ''} onChange={e => updateRuleRef(gi, ri, { artifact_id: e.target.value })} style={{ ...S.select, flex: 1, fontSize: '12px' }}>
                <option value="">— Select artifact —</option>
                {allArtifacts.map(a => <option key={a.id} value={a.id}>{a.name} ({TYPE_LABELS[a.type] || a.type})</option>)}
              </select>
              <button onClick={() => removeRuleRef(gi, ri)} style={{ ...S.btn('danger'), padding: '4px 10px' }}>✕</button>
            </div>
          ))}
          <button onClick={() => addRuleRef(gi)} style={{ ...S.btn('ghost'), fontSize: '12px' }}>+ Add Rule</button>
        </div>
      ))}
      <button onClick={addGroup} style={{ ...S.btn('ghost'), marginBottom: '16px' }}>+ Add Group</button>

      {!isNew && (
        <>
          <label style={S.label}>Version Notes</label>
          <input value={notes} onChange={e => setNotes(e.target.value)} style={{ ...S.input, marginBottom: '10px' }} placeholder="What changed?" />
        </>
      )}

      <button onClick={save} disabled={saving} style={S.btn('primary')}>
        {saving ? 'Saving…' : (isNew ? 'Create' : 'Save & Publish New Version')}
      </button>

      {!isNew && <EvaluatePanel workspaceId={workspaceId} ruleSetId={ruleSet.id} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SetsTab
// ---------------------------------------------------------------------------
function SetsTab({ workspaceId }) {
  const [sets, setSets] = useState([]);
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      apiFetch(`/api/rules/workspaces/${workspaceId}/sets`),
      apiFetch(`/api/rules/workspaces/${workspaceId}/artifacts`),
    ]).then(([s, a]) => { setSets(s); setArtifacts(a); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  if (selected === 'new') {
    return <SetDetail workspaceId={workspaceId} ruleSet={null} allArtifacts={artifacts} onBack={() => setSelected(null)} onSaved={() => { setSelected(null); load(); }} />;
  }
  if (selected) {
    return <SetDetail workspaceId={workspaceId} ruleSet={selected} allArtifacts={artifacts} onBack={() => setSelected(null)} onSaved={load} />;
  }

  return (
    <div>
      <div style={S.row}>
        <button onClick={() => setSelected('new')} style={S.btn('primary')}>+ New Rule Set</button>
      </div>
      {loading ? <div style={{ color: 'var(--text-secondary)' }}>Loading…</div> : null}
      {!loading && sets.length === 0 && (
        <div style={{ color: 'var(--text-secondary)', fontSize: '14px', padding: '40px 0', textAlign: 'center' }}>
          No rule sets yet.<br /><span style={{ fontSize: '12px' }}>A Rule Set groups artifacts into an ordered execution pipeline.</span>
        </div>
      )}
      {sets.map(rs => (
        <div key={rs.id} style={S.card} onClick={() => setSelected(rs)}
          onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent-color)'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
        >
          <span style={{ fontSize: '20px' }}>📦</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}>{rs.name}</div>
            {rs.description && <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{rs.description}</div>}
          </div>
          <span style={S.badge(rs.is_active ? '#22c55e' : '#71717a')}>{rs.is_active ? 'Active' : 'Inactive'}</span>
          {rs.current_version_id && <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>has version</span>}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TracesTab — enhanced with filters, names, JSON export, child trace tree
// ---------------------------------------------------------------------------
function TracesTab({ workspaceId }) {
  const [traces, setTraces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Filters
  const [filterSetName, setFilterSetName] = useState('');
  const [filterContextId, setFilterContextId] = useState('');
  const [filterErrorOnly, setFilterErrorOnly] = useState(false);
  const [filterSince, setFilterSince] = useState('');
  const [filterUntil, setFilterUntil] = useState('');

  // Reference data for selectors
  const [sets, setSets] = useState([]);
  const [artifacts, setArtifacts] = useState([]);

  useEffect(() => {
    Promise.all([
      apiFetch(`/api/rules/workspaces/${workspaceId}/sets`),
      apiFetch(`/api/rules/workspaces/${workspaceId}/artifacts`),
    ]).then(([s, a]) => { setSets(s); setArtifacts(a); }).catch(() => {});
  }, [workspaceId]);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: '200' });
    if (filterErrorOnly) params.set('error_only', 'true');
    if (filterContextId.trim()) params.set('context_id', filterContextId.trim());
    if (filterSince) params.set('since', filterSince);
    if (filterUntil) params.set('until', filterUntil);
    // Name filter is client-side after fetch
    apiFetch(`/api/rules/workspaces/${workspaceId}/evaluations?${params}`)
      .then(data => setTraces(data))
      .catch(() => setTraces([]))
      .finally(() => setLoading(false));
  }, [workspaceId, filterErrorOnly, filterContextId, filterSince, filterUntil]);

  useEffect(() => { load(); }, [load]);

  const openTrace = async (id) => {
    if (selected === id) { setSelected(null); setDetail(null); return; }
    setSelected(id);
    setDetail(null);
    setDetailLoading(true);
    const d = await apiFetch(`/api/rules/workspaces/${workspaceId}/evaluations/${id}`).catch(() => null);
    setDetail(d);
    setDetailLoading(false);
  };

  const exportTrace = () => {
    if (!detail) return;
    const blob = new Blob([JSON.stringify(detail, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `trace-${detail.id?.slice(0, 8)}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  // Client-side name filter
  const nameFilter = filterSetName.toLowerCase();
  const displayed = nameFilter
    ? traces.filter(ev => {
        const name = (ev.rule_set_name || ev.rule_artifact_name || '').toLowerCase();
        return name.includes(nameFilter);
      })
    : traces;

  const preStyle = { background: 'var(--bg-primary)', padding: '8px', borderRadius: '6px', fontSize: '11px', overflow: 'auto', marginBottom: '8px', maxHeight: '200px', margin: '0 0 8px 0' };

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '14px', padding: '12px 14px', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
        <div>
          <label style={S.label}>Name contains</label>
          <input value={filterSetName} onChange={e => setFilterSetName(e.target.value)} style={{ ...S.input, width: '160px' }} placeholder="rule set or artifact" />
        </div>
        <div>
          <label style={S.label}>Context ID</label>
          <input value={filterContextId} onChange={e => setFilterContextId(e.target.value)} style={{ ...S.input, width: '140px' }} placeholder="case or run ID" />
        </div>
        <div>
          <label style={S.label}>Since</label>
          <input type="datetime-local" value={filterSince} onChange={e => setFilterSince(e.target.value)} style={{ ...S.input, width: '160px' }} />
        </div>
        <div>
          <label style={S.label}>Until</label>
          <input type="datetime-local" value={filterUntil} onChange={e => setFilterUntil(e.target.value)} style={{ ...S.input, width: '160px' }} />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={filterErrorOnly} onChange={e => setFilterErrorOnly(e.target.checked)} />
          Errors only
        </label>
        <button onClick={load} style={S.btn('primary')}>↻ Apply</button>
        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginLeft: 'auto' }}>
          {displayed.length} result{displayed.length !== 1 ? 's' : ''}
          {displayed.length !== traces.length ? ` (filtered from ${traces.length})` : ''}
        </span>
      </div>

      {loading && <div style={{ color: 'var(--text-secondary)', marginBottom: '10px' }}>Loading…</div>}

      {!loading && displayed.length === 0 && (
        <div style={{ color: 'var(--text-secondary)', fontSize: '14px', padding: '40px 0', textAlign: 'center' }}>
          No evaluations recorded yet. Enable "trace" on the Rule Evaluate node or API call to start logging.
        </div>
      )}

      <div style={{ display: 'flex', gap: '16px' }}>
        {/* Trace list */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {displayed.map(ev => {
            const name = ev.rule_set_name || ev.rule_artifact_name || (ev.rule_set_id ? ev.rule_set_id.slice(0, 8) + '…' : ev.rule_artifact_id?.slice(0, 8) + '…');
            const kind = ev.rule_set_id ? 'Set' : 'Artifact';
            return (
              <div key={ev.id}
                style={{ ...S.card, background: selected === ev.id ? 'var(--bg-input)' : 'var(--bg-secondary)', borderColor: selected === ev.id ? 'var(--accent-color)' : 'var(--border-color)' }}
                onClick={() => openTrace(ev.id)}
              >
                <span style={S.badge(ev.error_text ? '#ef4444' : ev.matched ? '#22c55e' : '#71717a')}>
                  {ev.error_text ? 'ERROR' : ev.matched ? 'MATCHED' : 'NO MATCH'}
                </span>
                <div style={{ flex: 1, minWidth: 0, fontSize: '12px' }}>
                  <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>{kind}: </span>{name}
                  </div>
                  {ev.context_id && <div style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>context: {ev.context_id}</div>}
                </div>
                {ev.duration_ms != null && <span style={{ fontSize: '11px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{ev.duration_ms}ms</span>}
                <span style={{ fontSize: '11px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{ev.created_at?.slice(0, 19).replace('T', ' ')}</span>
              </div>
            );
          })}
        </div>

        {/* Detail panel */}
        {(detail || detailLoading) && (
          <div style={{ width: '420px', flexShrink: 0, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px', position: 'sticky', top: 0, alignSelf: 'flex-start', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontWeight: 700 }}>Trace Detail</span>
              <div style={{ display: 'flex', gap: '6px' }}>
                {detail && <button onClick={exportTrace} style={{ ...S.btn('ghost'), fontSize: '11px', padding: '3px 8px' }}>⬇ Export JSON</button>}
                <button onClick={() => { setSelected(null); setDetail(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '16px' }}>✕</button>
              </div>
            </div>

            {detailLoading && <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading…</div>}

            {detail && (
              <>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
                  <span style={S.badge(detail.error_text ? '#ef4444' : detail.matched ? '#22c55e' : '#71717a')}>
                    {detail.error_text ? 'ERROR' : detail.matched ? 'matched' : 'no match'}
                  </span>
                  {detail.duration_ms != null && <span style={{ fontSize: '11px', color: 'var(--text-secondary)', alignSelf: 'center' }}>{detail.duration_ms}ms</span>}
                  {detail.rule_set_name && <span style={S.badge('#7c7cff')}>{detail.rule_set_name}</span>}
                  {detail.rule_artifact_name && <span style={S.badge('#f59e0b')}>{detail.rule_artifact_name}</span>}
                </div>

                {detail.error_text && <div style={{ ...S.error, marginBottom: '10px', fontSize: '11px' }}>{detail.error_text}</div>}

                {detail.input && Object.keys(detail.input).length > 0 && (
                  <><div style={S.label}>Input</div><pre style={preStyle}>{JSON.stringify(detail.input, null, 2)}</pre></>
                )}
                {detail.output && Object.keys(detail.output).length > 0 && (
                  <><div style={S.label}>Output</div><pre style={preStyle}>{JSON.stringify(detail.output, null, 2)}</pre></>
                )}

                {detail.actions_taken?.length > 0 && (
                  <>
                    <div style={S.label}>Actions Taken ({detail.actions_taken.length})</div>
                    {detail.actions_taken.map((a, i) => (
                      <div key={i} style={{ fontSize: '11px', color: 'var(--text-secondary)', paddingLeft: '8px', marginBottom: '3px' }}>
                        • <strong>{a.type}</strong>{a.path ? `: ${a.path} = ${JSON.stringify(a.value)}` : a.tag ? `: ${a.tag}` : a.outputs ? `: ${JSON.stringify(a.outputs).slice(0, 50)}` : ''}
                      </div>
                    ))}
                  </>
                )}

                {detail.children?.length > 0 && (
                  <>
                    <div style={{ ...S.divider, margin: '12px 0' }} />
                    <div style={S.label}>Child Traces ({detail.children.length})</div>
                    {detail.children.map((c, i) => (
                      <div key={c.id || i} style={{ padding: '6px 8px', background: 'var(--bg-primary)', borderRadius: '6px', marginBottom: '4px', fontSize: '11px' }}>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <span style={S.badge(c.error_text ? '#ef4444' : c.matched ? '#22c55e' : '#71717a')}>
                            {c.error_text ? 'ERR' : c.matched ? '✓' : '—'}
                          </span>
                          <span style={{ fontWeight: 600 }}>{c.rule_artifact_name || c.rule_artifact_id?.slice(0, 8)}</span>
                          {c.duration_ms != null && <span style={{ color: 'var(--text-secondary)', marginLeft: 'auto' }}>{c.duration_ms}ms</span>}
                        </div>
                        {c.error_text && <div style={{ color: '#fca5a5', marginTop: '2px' }}>{c.error_text}</div>}
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HistoricalReplayTab
// ---------------------------------------------------------------------------
function HistoricalReplayTab({ workspaceId }) {
  const [sets, setSets] = useState([]);
  const [setId, setSetId] = useState('');
  const [versions, setVersions] = useState([]);
  const [versionId, setVersionId] = useState('');
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [limit, setLimit] = useState(50);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    apiFetch(`/api/rules/workspaces/${workspaceId}/sets`)
      .then(setSets).catch(() => {});
  }, [workspaceId]);

  useEffect(() => {
    if (!setId) { setVersions([]); setVersionId(''); return; }
    apiFetch(`/api/rules/workspaces/${workspaceId}/sets/${setId}/versions`)
      .then(vs => { setVersions(vs); setVersionId(''); }).catch(() => {});
  }, [setId, workspaceId]);

  const run = async () => {
    if (!setId || !versionId) return;
    setErr(''); setResults(null); setRunning(true);
    try {
      const body = { limit };
      if (since) body.since = since;
      if (until) body.until = until;
      const data = await apiFetch(
        `/api/rules/workspaces/${workspaceId}/sets/${setId}/versions/${versionId}/replay`,
        { method: 'POST', body: JSON.stringify(body) }
      );
      setResults(data);
    } catch (e) { setErr(e.message); }
    setRunning(false);
  };

  return (
    <div>
      <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
        Re-run historical trace inputs through a proposed version and diff outputs against what was recorded at evaluation time.
      </div>

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '16px' }}>
        <div style={{ flex: 1, minWidth: '180px' }}>
          <label style={S.label}>Rule Set</label>
          <select value={setId} onChange={e => setSetId(e.target.value)} style={{ ...S.select, width: '100%' }}>
            <option value="">— Select —</option>
            {sets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: '180px' }}>
          <label style={S.label}>Proposed Version</label>
          <select value={versionId} onChange={e => setVersionId(e.target.value)} style={{ ...S.select, width: '100%' }} disabled={!setId}>
            <option value="">— Select Version —</option>
            {versions.map(v => <option key={v.id} value={v.id}>v{v.version} {v.is_published ? '(published)' : '(draft)'} {v.notes ? `— ${v.notes}` : ''}</option>)}
          </select>
        </div>
        <div>
          <label style={S.label}>Since</label>
          <input type="datetime-local" value={since} onChange={e => setSince(e.target.value)} style={{ ...S.input, width: '160px' }} />
        </div>
        <div>
          <label style={S.label}>Until</label>
          <input type="datetime-local" value={until} onChange={e => setUntil(e.target.value)} style={{ ...S.input, width: '160px' }} />
        </div>
        <div>
          <label style={S.label}>Max traces</label>
          <input type="number" value={limit} min={1} max={200} onChange={e => setLimit(Number(e.target.value))} style={{ ...S.input, width: '80px' }} />
        </div>
        <button onClick={run} disabled={running || !setId || !versionId} style={S.btn('primary')}>
          {running ? 'Replaying…' : '▶ Replay'}
        </button>
      </div>

      {err && <div style={S.error}>{err}</div>}

      {results && (
        <div>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '14px', padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
            <span style={S.badge('#71717a')}>{results.total} replayed</span>
            <span style={S.badge('#f59e0b')}>{results.changed} changed</span>
            <span style={S.badge('#22c55e')}>{results.total - results.changed} unchanged</span>
          </div>

          {results.results.map((r, i) => (
            <div key={r.trace_id || i} style={{ ...S.card, cursor: 'default', flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                <span style={S.badge(r.error ? '#ef4444' : r.changed ? '#f59e0b' : '#22c55e')}>
                  {r.error ? 'ERROR' : r.changed ? 'CHANGED' : 'SAME'}
                </span>
                <span style={{ fontSize: '12px', fontWeight: 600 }}>
                  {r.evaluated_at?.slice(0, 19).replace('T', ' ')}
                </span>
                {r.context_id && <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>context: {r.context_id}</span>}
                <div style={{ flex: 1 }} />
                {r.duration_ms != null && <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{r.duration_ms}ms</span>}
              </div>
              {r.error && <div style={{ ...S.error, fontSize: '11px', margin: 0 }}>{r.error}</div>}
              {r.changed && !r.error && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', width: '100%' }}>
                  <div>
                    <div style={{ ...S.label, color: '#71717a' }}>Old outputs</div>
                    <pre style={{ background: 'var(--bg-primary)', padding: '6px', borderRadius: '4px', fontSize: '11px', overflow: 'auto', margin: 0, maxHeight: '120px' }}>{JSON.stringify(r.old_outputs, null, 2)}</pre>
                  </div>
                  <div>
                    <div style={{ ...S.label, color: '#22c55e' }}>New outputs</div>
                    <pre style={{ background: 'var(--bg-primary)', padding: '6px', borderRadius: '4px', fontSize: '11px', overflow: 'auto', margin: 0, maxHeight: '120px', border: '1px solid rgba(34,197,94,0.3)' }}>{JSON.stringify(r.new_outputs, null, 2)}</pre>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Simulator — Single Input
// ---------------------------------------------------------------------------
function SingleInputSimulator({ workspaceId }) {
  const [targetType, setTargetType] = useState('artifact');
  const [artifacts, setArtifacts] = useState([]);
  const [sets, setSets] = useState([]);
  const [targetId, setTargetId] = useState('');
  const [inputText, setInputText] = useState('{}');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    Promise.all([
      apiFetch(`/api/rules/workspaces/${workspaceId}/artifacts`),
      apiFetch(`/api/rules/workspaces/${workspaceId}/sets`),
    ]).then(([a, s]) => { setArtifacts(a); setSets(s); }).catch(() => {});
  }, [workspaceId]);

  const run = async () => {
    setErr(''); setResult(null); setLoading(true);
    try {
      let input = {};
      try { input = JSON.parse(inputText); } catch { throw new Error('Input JSON is invalid'); }
      const path = targetType === 'artifact'
        ? `/api/rules/workspaces/${workspaceId}/artifacts/${targetId}/evaluate`
        : `/api/rules/workspaces/${workspaceId}/sets/${targetId}/evaluate`;
      const res = await apiFetch(path, { method: 'POST', body: JSON.stringify({ input, trace: false }) });
      setResult(res);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };

  const targets = targetType === 'artifact' ? artifacts : sets;

  return (
    <div>
      <div style={{ display: 'flex', gap: '12px', marginBottom: '14px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label style={S.label}>Target Type</label>
          <select value={targetType} onChange={e => { setTargetType(e.target.value); setTargetId(''); setResult(null); }} style={S.select}>
            <option value="artifact">Rule Artifact</option>
            <option value="set">Rule Set</option>
          </select>
        </div>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <label style={S.label}>Select {targetType === 'artifact' ? 'Artifact' : 'Rule Set'}</label>
          <select value={targetId} onChange={e => setTargetId(e.target.value)} style={{ ...S.select, width: '100%' }}>
            <option value="">— Select —</option>
            {targets.map(t => <option key={t.id} value={t.id}>{t.name} {t.type ? `(${TYPE_LABELS[t.type] || t.type})` : ''}</option>)}
          </select>
        </div>
      </div>
      <label style={S.label}>Input JSON</label>
      <textarea value={inputText} onChange={e => setInputText(e.target.value)} style={{ ...S.textarea, minHeight: '100px', marginBottom: '10px' }} placeholder='{"credit_score": 720, "income": 80000}' />
      {err && <div style={S.error}>{err}</div>}
      <button onClick={run} disabled={loading || !targetId} style={S.btn('primary')}>{loading ? 'Running…' : '▶ Run'}</button>

      {result && (
        <div style={{ marginTop: '16px' }}>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'center' }}>
            <span style={S.badge(result.matched !== false ? '#22c55e' : '#71717a')}>{result.matched !== false ? 'MATCHED' : 'NO MATCH'}</span>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{result.duration_ms}ms</span>
          </div>

          {result.outputs && Object.keys(result.outputs).length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <div style={S.label}>Outputs</div>
              <pre style={{ background: 'var(--bg-secondary)', padding: '10px', borderRadius: '6px', fontSize: '12px', overflow: 'auto', margin: 0 }}>{JSON.stringify(result.outputs, null, 2)}</pre>
            </div>
          )}

          {result.score !== undefined && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '30px', fontWeight: 700, color: 'var(--accent-color)' }}>{result.score}</span>
                <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>→</span>
                {result.decision && <span style={S.badge('#22c55e')}>{result.decision}</span>}
              </div>
              {result.contributions?.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead><tr>{['Factor', 'Raw Value', 'Points', 'Weight', 'Contribution'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)', fontSize: '11px' }}>{h}</th>
                  ))}</tr></thead>
                  <tbody>{result.contributions.map((c, i) => (
                    <tr key={i}>
                      <td style={{ padding: '4px 8px' }}>{c.label}</td>
                      <td style={{ padding: '4px 8px' }}>{String(c.raw_value ?? '—')}</td>
                      <td style={{ padding: '4px 8px' }}>{c.points}</td>
                      <td style={{ padding: '4px 8px' }}>{c.weight}</td>
                      <td style={{ padding: '4px 8px', fontWeight: 600 }}>{c.contribution}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>
          )}

          {result.path?.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <div style={S.label}>Tree Path</div>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                {result.path.map((p, i) => <span key={i} style={S.badge('#06b6d4')}>{p}</span>)}
              </div>
            </div>
          )}

          {result.matched_rules?.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <div style={S.label}>Matched Rules ({result.matched_rules.length})</div>
              {result.matched_rules.map((r, i) => <div key={i} style={{ fontSize: '12px', color: 'var(--text-secondary)', paddingLeft: '8px' }}>• {r}</div>)}
            </div>
          )}

          {result.validation_errors?.length > 0 && result.validation_errors.map((e, i) => (
            <div key={i} style={S.error}>{e.field}: {e.message}</div>
          ))}
          {result.errors?.length > 0 && <div style={S.error}>{result.errors.join('; ')}</div>}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Simulator — Fixture Sets
// ---------------------------------------------------------------------------
function FixtureSetsSimulator({ workspaceId }) {
  const [fixtureSets, setFixtureSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [newName, setNewName] = useState('');
  const [newFixtures, setNewFixtures] = useState([{ label: 'Test 1', input: '{}', expected_output: '' }]);
  const [targetType, setTargetType] = useState('artifact');
  const [targetId, setTargetId] = useState('');
  const [artifacts, setArtifacts] = useState([]);
  const [sets, setSets] = useState([]);
  const [simResults, setSimResults] = useState(null);
  const [simRunning, setSimRunning] = useState(false);
  const [simErr, setSimErr] = useState('');

  const loadFixtureSets = useCallback(() => {
    setLoading(true);
    apiFetch(`/api/rules/workspaces/${workspaceId}/fixture-sets`)
      .then(data => setFixtureSets(data))
      .catch(() => setFixtureSets([]))
      .finally(() => setLoading(false));
  }, [workspaceId]);

  useEffect(() => {
    loadFixtureSets();
    Promise.all([
      apiFetch(`/api/rules/workspaces/${workspaceId}/artifacts`),
      apiFetch(`/api/rules/workspaces/${workspaceId}/sets`),
    ]).then(([a, s]) => { setArtifacts(a); setSets(s); }).catch(() => {});
  }, [workspaceId, loadFixtureSets]);

  const createFixtureSet = async () => {
    try {
      const fixtures = newFixtures.map(f => {
        const fixture = { label: f.label, input: JSON.parse(f.input || '{}') };
        if (f.expected_output?.trim()) fixture.expected_output = JSON.parse(f.expected_output);
        return fixture;
      });
      await apiFetch(`/api/rules/workspaces/${workspaceId}/fixture-sets`, {
        method: 'POST',
        body: JSON.stringify({ name: newName, fixtures }),
      });
      setCreating(false);
      setNewName('');
      setNewFixtures([{ label: 'Test 1', input: '{}', expected_output: '' }]);
      loadFixtureSets();
    } catch (e) { alert(e.message); }
  };

  const runSimulation = async () => {
    if (!targetId) { setSimErr('Select an artifact or rule set first'); return; }
    setSimErr(''); setSimResults(null); setSimRunning(true);
    try {
      const path = targetType === 'set'
        ? `/api/rules/workspaces/${workspaceId}/sets/${targetId}/simulate`
        : `/api/rules/workspaces/${workspaceId}/artifacts/${targetId}/simulate`;
      const res = await apiFetch(path, { method: 'POST', body: JSON.stringify({ fixture_set_id: selected.id }) });
      setSimResults(res);
    } catch (e) { setSimErr(e.message); }
    setSimRunning(false);
  };

  if (creating) {
    return (
      <div>
        <div style={{ ...S.row, marginBottom: '16px' }}>
          <button onClick={() => setCreating(false)} style={S.btn('ghost')}>← Cancel</button>
          <h3 style={{ margin: 0, fontSize: '15px' }}>New Fixture Set</h3>
        </div>
        <label style={S.label}>Name</label>
        <input value={newName} onChange={e => setNewName(e.target.value)} style={{ ...S.input, marginBottom: '16px' }} placeholder="e.g. Credit scoring test cases" />
        <div style={S.sectionTitle}>Fixtures</div>
        {newFixtures.map((f, i) => (
          <div key={i} style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '12px', marginBottom: '8px' }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
              <input value={f.label} onChange={e => { const fs = [...newFixtures]; fs[i] = { ...f, label: e.target.value }; setNewFixtures(fs); }} style={{ ...S.input, flex: 1 }} placeholder="Label" />
              {newFixtures.length > 1 && <button onClick={() => setNewFixtures(newFixtures.filter((_, idx) => idx !== i))} style={S.btn('danger')}>✕</button>}
            </div>
            <label style={S.label}>Input JSON</label>
            <textarea value={f.input} onChange={e => { const fs = [...newFixtures]; fs[i] = { ...f, input: e.target.value }; setNewFixtures(fs); }} style={{ ...S.textarea, minHeight: '56px', marginBottom: '8px' }} placeholder='{"credit_score": 720}' />
            <label style={S.label}>Expected Output JSON (optional — used for pass/fail assertion)</label>
            <textarea value={f.expected_output} onChange={e => { const fs = [...newFixtures]; fs[i] = { ...f, expected_output: e.target.value }; setNewFixtures(fs); }} style={{ ...S.textarea, minHeight: '48px' }} placeholder='{"decision": "auto_approve"}' />
          </div>
        ))}
        <button onClick={() => setNewFixtures([...newFixtures, { label: `Test ${newFixtures.length + 1}`, input: '{}', expected_output: '' }])} style={{ ...S.btn('ghost'), marginBottom: '16px' }}>+ Add Fixture</button>
        <br />
        <button onClick={createFixtureSet} disabled={!newName.trim()} style={S.btn('primary')}>Create</button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: '20px' }}>
      <div style={{ width: '240px', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <span style={{ fontWeight: 600, fontSize: '13px' }}>Fixture Sets</span>
          <button onClick={() => setCreating(true)} style={{ ...S.btn('ghost'), fontSize: '12px', padding: '4px 10px' }}>+ New</button>
        </div>
        {loading && <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Loading…</div>}
        {!loading && fixtureSets.length === 0 && (
          <div style={{ color: 'var(--text-secondary)', fontSize: '12px', textAlign: 'center', padding: '24px 0' }}>No fixture sets yet.</div>
        )}
        {fixtureSets.map(fs => (
          <div key={fs.id} onClick={() => { setSelected(fs); setSimResults(null); setSimErr(''); }} style={{
            ...S.card, cursor: 'pointer',
            background: selected?.id === fs.id ? 'var(--bg-input)' : 'var(--bg-secondary)',
            borderColor: selected?.id === fs.id ? 'var(--accent-color)' : 'var(--border-color)',
          }}>
            <span style={{ fontSize: '16px' }}>📋</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fs.name}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{fs.fixture_count} fixture{fs.fixture_count !== 1 ? 's' : ''}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ flex: 1 }}>
        {!selected ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: 'var(--text-secondary)', fontSize: '14px' }}>
            Select a fixture set to run simulations
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '14px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div>
                <label style={S.label}>Run Against</label>
                <select value={targetType} onChange={e => { setTargetType(e.target.value); setTargetId(''); }} style={S.select}>
                  <option value="artifact">Rule Artifact</option>
                  <option value="set">Rule Set</option>
                </select>
              </div>
              <div style={{ flex: 1, minWidth: '180px' }}>
                <label style={S.label}>Select {targetType === 'artifact' ? 'Artifact' : 'Rule Set'}</label>
                <select value={targetId} onChange={e => setTargetId(e.target.value)} style={{ ...S.select, width: '100%' }}>
                  <option value="">— Select —</option>
                  {(targetType === 'artifact' ? artifacts : sets).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <button onClick={runSimulation} disabled={simRunning || !targetId} style={S.btn('primary')}>
                {simRunning ? 'Running…' : '▶ Run All'}
              </button>
            </div>

            {simErr && <div style={S.error}>{simErr}</div>}

            {simResults && (
              <div style={{ display: 'flex', gap: '12px', marginBottom: '14px', padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                <span style={S.badge('#22c55e')}>✓ {simResults.passed} passed</span>
                <span style={S.badge('#ef4444')}>✗ {simResults.errored} errors</span>
                <span style={S.badge('#71717a')}>{simResults.total} total</span>
              </div>
            )}

            <div style={S.sectionTitle}>Fixtures in "{selected.name}"</div>
            {(selected.fixtures || []).map((f, i) => {
              const r = simResults?.results?.[i];
              const statusColor = !r ? '#71717a' : r.error ? '#ef4444' : r.passed ? '#22c55e' : '#f59e0b';
              const statusLabel = !r ? '—' : r.error ? 'ERROR' : r.passed ? 'PASS' : 'FAIL';
              return (
                <div key={i} style={{ ...S.card, cursor: 'default', flexDirection: 'column', alignItems: 'flex-start', gap: '6px' }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                    <span style={{ fontWeight: 600, fontSize: '13px' }}>{f.label || `Fixture ${i + 1}`}</span>
                    <span style={S.badge(statusColor)}>{statusLabel}</span>
                    <div style={{ flex: 1 }} />
                    {r?.duration_ms != null && <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{r.duration_ms}ms</span>}
                  </div>
                  {r && (
                    <div style={{ fontSize: '12px', width: '100%' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>Output: </span>
                      <code style={{ fontSize: '11px' }}>{JSON.stringify(r.outputs || {})}</code>
                      {f.expected_output && (
                        <>
                          <span style={{ color: 'var(--text-secondary)', marginLeft: '8px' }}>Expected: </span>
                          <code style={{ fontSize: '11px' }}>{JSON.stringify(f.expected_output)}</code>
                        </>
                      )}
                      {r.error && <div style={{ ...S.error, marginTop: '4px', fontSize: '11px' }}>{r.error}</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SimulatorTab
// ---------------------------------------------------------------------------
function SimulatorTab({ workspaceId }) {
  const [subTab, setSubTab] = useState('single');
  return (
    <div>
      <div style={{ display: 'flex', gap: '2px', marginBottom: '20px' }}>
        {[['single', '⚡ Single Input'], ['fixtures', '📋 Fixture Sets'], ['replay', '🕐 Historical Replay']].map(([id, label]) => (
          <button key={id} style={S.tab(subTab === id)} onClick={() => setSubTab(id)}>{label}</button>
        ))}
      </div>
      {subTab === 'single' && <SingleInputSimulator workspaceId={workspaceId} />}
      {subTab === 'fixtures' && <FixtureSetsSimulator workspaceId={workspaceId} />}
      {subTab === 'replay' && <HistoricalReplayTab workspaceId={workspaceId} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Rules component
// ---------------------------------------------------------------------------
// ===========================================================================
// Phase 5 — A/B Testing Tab
// ===========================================================================

function ABTestsTab({ workspaceId }) {
  const [sets, setSets] = useState([]);
  const [selectedSetId, setSelectedSetId] = useState('');
  const [setVersions, setSetVersions] = useState([]);
  const [abTests, setAbTests] = useState([]);
  const [loadingTests, setLoadingTests] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  // Create form
  const [versionA, setVersionA] = useState('');
  const [versionB, setVersionB] = useState('');
  const [splitPct, setSplitPct] = useState(50);
  const [testName, setTestName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    apiFetch(`/api/rules/workspaces/${workspaceId}/sets`)
      .then(setSets)
      .catch(() => setSets([]));
  }, [workspaceId]);

  useEffect(() => {
    if (!selectedSetId) { setSetVersions([]); setAbTests([]); return; }
    Promise.all([
      apiFetch(`/api/rules/workspaces/${workspaceId}/sets/${selectedSetId}/versions`),
      apiFetch(`/api/rules/workspaces/${workspaceId}/sets/${selectedSetId}/ab-tests`).catch(() => []),
    ]).then(([vs, tests]) => { setSetVersions(vs); setAbTests(tests); })
      .catch(() => {});
  }, [workspaceId, selectedSetId]);

  const loadTests = () => {
    if (!selectedSetId) return;
    setLoadingTests(true);
    apiFetch(`/api/rules/workspaces/${workspaceId}/sets/${selectedSetId}/ab-tests`)
      .then(setAbTests)
      .catch(() => setAbTests([]))
      .finally(() => setLoadingTests(false));
  };

  const createTest = async () => {
    if (!selectedSetId || !versionA || !versionB) { setErr('Select a rule set, version A and version B.'); return; }
    setCreating(true); setErr(''); setMsg('');
    try {
      await apiFetch(`/api/rules/workspaces/${workspaceId}/sets/${selectedSetId}/ab-tests`, {
        method: 'POST',
        body: JSON.stringify({ version_a_id: versionA, version_b_id: versionB, split_pct_b: splitPct, name: testName }),
      });
      setMsg('A/B test started!');
      loadTests();
    } catch (e) { setErr(e.message); }
    setCreating(false);
  };

  const promote = async (abId) => {
    try {
      await apiFetch(`/api/rules/workspaces/${workspaceId}/sets/${selectedSetId}/ab-tests/${abId}/promote`, { method: 'POST' });
      setMsg('Winner promoted to current version!');
      loadTests();
    } catch (e) { setErr(e.message); }
  };

  const endTest = async (abId) => {
    try {
      await apiFetch(`/api/rules/workspaces/${workspaceId}/sets/${selectedSetId}/ab-tests/${abId}/end`, { method: 'POST' });
      loadTests();
    } catch (e) { setErr(e.message); }
  };

  const versionLabel = (vid) => {
    const v = setVersions.find(v => v.id === vid);
    return v ? `v${v.version}${v.is_published ? ' (published)' : ''}` : vid?.slice(0, 8) || '—';
  };

  return (
    <div>
      <div style={{ ...S.row, marginBottom: '20px' }}>
        <select value={selectedSetId} onChange={e => { setSelectedSetId(e.target.value); setErr(''); setMsg(''); }} style={{ ...S.select, flex: 1 }}>
          <option value="">— Select a Rule Set —</option>
          {sets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      {err && <div style={S.error}>{err}</div>}
      {msg && <div style={S.success}>{msg}</div>}

      {selectedSetId && (
        <>
          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '18px', marginBottom: '24px' }}>
            <div style={S.sectionTitle}>Start New A/B Test</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={S.label}>Version A (control)</label>
                <select value={versionA} onChange={e => setVersionA(e.target.value)} style={{ ...S.select, width: '100%' }}>
                  <option value="">— Select —</option>
                  {setVersions.map(v => <option key={v.id} value={v.id}>v{v.version}{v.is_published ? ' ✓' : ''}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Version B (challenger)</label>
                <select value={versionB} onChange={e => setVersionB(e.target.value)} style={{ ...S.select, width: '100%' }}>
                  <option value="">— Select —</option>
                  {setVersions.map(v => <option key={v.id} value={v.id}>v{v.version}{v.is_published ? ' ✓' : ''}</option>)}
                </select>
              </div>
            </div>
            <label style={S.label}>Traffic to B: {splitPct}%</label>
            <input type="range" min={0} max={100} value={splitPct} onChange={e => setSplitPct(Number(e.target.value))} style={{ width: '100%', marginBottom: '10px' }} />
            <label style={S.label}>Test Name (optional)</label>
            <input value={testName} onChange={e => setTestName(e.target.value)} placeholder="e.g. Auto-assign v2 vs v3" style={{ ...S.input, marginBottom: '12px' }} />
            <button onClick={createTest} disabled={creating} style={S.btn('primary')}>{creating ? 'Starting…' : '▶ Start A/B Test'}</button>
          </div>

          <div style={S.sectionTitle}>Test History {loadingTests && '…'}</div>
          {abTests.length === 0 && <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>No A/B tests for this rule set yet.</p>}
          {abTests.map(ab => {
            const metrics = ab.metrics || {};
            const arm = (arm) => metrics[arm] || { evals: 0, matched: 0, errors: 0, avg_ms: 0 };
            const aM = arm('a'); const bM = arm('b');
            return (
              <div key={ab.id} style={{ ...S.card, flexDirection: 'column', alignItems: 'stretch', gap: '10px' }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span style={{ fontWeight: 700 }}>{ab.name || 'Unnamed Test'}</span>
                  <span style={S.badge(ab.status === 'active' ? '#22c55e' : '#71717a')}>{ab.status}</span>
                  {ab.winner_version_id && <span style={S.badge('#06b6d4')}>Winner: {versionLabel(ab.winner_version_id)}</span>}
                  <span style={{ flex: 1 }} />
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{ab.started_at?.slice(0, 10)}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {[['a', aM, ab.version_a_id], ['b', bM, ab.version_b_id]].map(([arm, m, vid]) => (
                    <div key={arm} style={{ background: 'var(--bg-primary)', borderRadius: '6px', padding: '10px' }}>
                      <div style={{ fontWeight: 700, marginBottom: '6px', color: arm === 'a' ? '#06b6d4' : '#a855f7' }}>
                        Arm {arm.toUpperCase()} — {versionLabel(vid)} ({arm === 'a' ? `${100 - ab.split_pct_b}%` : `${ab.split_pct_b}%`} traffic)
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                        <span>Evaluations: <b style={{ color: 'var(--text-primary)' }}>{m.evals}</b></span>
                        <span>Matched: <b style={{ color: '#22c55e' }}>{m.matched}</b></span>
                        <span>Errors: <b style={{ color: '#ef4444' }}>{m.errors}</b></span>
                        <span>Avg ms: <b style={{ color: 'var(--text-primary)' }}>{m.avg_ms}</b></span>
                      </div>
                    </div>
                  ))}
                </div>
                {ab.status === 'active' && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => promote(ab.id)} style={S.btn('primary')}>🏆 Promote Winner</button>
                    <button onClick={() => endTest(ab.id)} style={S.btn('ghost')}>■ End Test</button>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

// ===========================================================================
// Phase 6 — Governance Dashboard Tab
// ===========================================================================
function GovernanceTab({ workspaceId }) {
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [reviewNotes, setReviewNotes] = useState({});
  const [editingSet, setEditingSet] = useState(null); // set id being edited for required approvers
  const [editApprovers, setEditApprovers] = useState(1);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch(`/api/rules/workspaces/${workspaceId}/governance/dashboard`)
      .then(setDash)
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const decide = async (type, artifactId, setId, versionId, action) => {
    setErr(''); setMsg('');
    const noteKey = `${type}-${versionId}`;
    const notes = reviewNotes[noteKey] || '';
    try {
      if (type === 'artifact') {
        await apiFetch(`/api/rules/workspaces/${workspaceId}/artifacts/${artifactId}/versions/${versionId}/${action}`, {
          method: 'POST', body: JSON.stringify({ notes }),
        });
      } else {
        await apiFetch(`/api/rules/workspaces/${workspaceId}/sets/${setId}/versions/${versionId}/${action}`, {
          method: 'POST', body: JSON.stringify({ notes }),
        });
      }
      setMsg(`Version ${action === 'approve' ? 'approved and published' : 'rejected'}.`);
      load();
    } catch (e) { setErr(e.message); }
  };

  const saveApprovers = async (setId) => {
    try {
      await apiFetch(`/api/rules/workspaces/${workspaceId}/sets/${setId}`, {
        method: 'PATCH', body: JSON.stringify({ required_approvers_count: editApprovers }),
      });
      setMsg('Required approvers updated.');
      setEditingSet(null);
      load();
    } catch (e) { setErr(e.message); }
  };

  const stats = dash?.stats || {};
  const pendingAv = dash?.pending_artifact_versions || [];
  const pendingSv = dash?.pending_set_versions || [];
  const activity = dash?.recent_activity || [];
  const highRisk = dash?.high_risk_sets || [];

  return (
    <div>
      {err && <div style={S.error}>{err}</div>}
      {msg && <div style={S.success}>{msg}</div>}

      {/* Stats bar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: '12px', marginBottom: '24px' }}>
        {[
          { label: 'Pending', value: stats.pending_total ?? 0, color: stats.pending_total > 0 ? '#f59e0b' : '#71717a' },
          { label: 'Pending Artifacts', value: stats.pending_artifacts ?? 0, color: '#06b6d4' },
          { label: 'Pending Sets', value: stats.pending_sets ?? 0, color: '#8b5cf6' },
          { label: 'Approved (7d)', value: stats.approved_this_week ?? 0, color: '#22c55e' },
          { label: 'Rejected (7d)', value: stats.rejected_this_week ?? 0, color: '#ef4444' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '14px 16px' }}>
            <div style={{ fontSize: '22px', fontWeight: 700, color }}>{value}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>{label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Left: Pending queue */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <div style={S.sectionTitle}>Approval Queue</div>
            <button onClick={load} style={{ ...S.btn('ghost'), padding: '3px 8px', fontSize: '11px', marginLeft: 'auto' }}>↺</button>
          </div>
          {loading && <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Loading…</p>}
          {!loading && pendingAv.length === 0 && pendingSv.length === 0 && (
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>No pending approvals.</p>
          )}

          {pendingAv.map(v => (
            <div key={v.id} style={{ ...S.card, flexDirection: 'column', alignItems: 'stretch', cursor: 'default', marginBottom: '8px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={S.badge('#06b6d4')}>artifact</span>
                <span style={{ fontWeight: 600, flex: 1, fontSize: '13px' }}>{v.artifact_name}</span>
                <span style={S.badge('#71717a')}>v{v.version}</span>
                <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{v.created_at?.slice(0, 10)}</span>
              </div>
              {v.notes && <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>{v.notes}</div>}
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '6px' }}>
                <input value={reviewNotes[`artifact-${v.id}`] || ''} onChange={e => setReviewNotes(n => ({ ...n, [`artifact-${v.id}`]: e.target.value }))} placeholder="Review notes (optional)" style={{ ...S.input, flex: 1, fontSize: '11px', padding: '5px 8px' }} />
                <button onClick={() => decide('artifact', v.artifact_id, null, v.id, 'approve')} style={{ ...S.btn('primary'), background: '#22c55e', padding: '5px 10px', fontSize: '12px' }}>✓</button>
                <button onClick={() => decide('artifact', v.artifact_id, null, v.id, 'reject')} style={{ ...S.btn('danger'), padding: '5px 10px', fontSize: '12px' }}>✕</button>
              </div>
            </div>
          ))}

          {pendingSv.map(v => (
            <div key={v.id} style={{ ...S.card, flexDirection: 'column', alignItems: 'stretch', cursor: 'default', marginBottom: '8px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={S.badge('#8b5cf6')}>rule set</span>
                <span style={{ fontWeight: 600, flex: 1, fontSize: '13px' }}>{v.set_name}</span>
                <span style={S.badge('#71717a')}>v{v.version}</span>
                <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{v.created_at?.slice(0, 10)}</span>
              </div>
              {v.notes && <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>{v.notes}</div>}
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '6px' }}>
                <input value={reviewNotes[`set-${v.id}`] || ''} onChange={e => setReviewNotes(n => ({ ...n, [`set-${v.id}`]: e.target.value }))} placeholder="Review notes (optional)" style={{ ...S.input, flex: 1, fontSize: '11px', padding: '5px 8px' }} />
                <button onClick={() => decide('set', null, v.rule_set_id, v.id, 'approve')} style={{ ...S.btn('primary'), background: '#22c55e', padding: '5px 10px', fontSize: '12px' }}>✓</button>
                <button onClick={() => decide('set', null, v.rule_set_id, v.id, 'reject')} style={{ ...S.btn('danger'), padding: '5px 10px', fontSize: '12px' }}>✕</button>
              </div>
            </div>
          ))}
        </div>

        {/* Right: Activity feed + High-risk sets */}
        <div>
          {/* Recent activity */}
          <div style={{ ...S.sectionTitle, marginBottom: '10px' }}>Recent Activity</div>
          {activity.length === 0 && !loading && <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px' }}>No review activity in the past 7 days.</p>}
          {activity.slice(0, 8).map((a, i) => (
            <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid var(--border-color)' }}>
              <span style={S.badge(a.action === 'approved' ? '#22c55e' : '#ef4444')}>{a.action}</span>
              <span style={{ fontSize: '13px', fontWeight: 600, flex: 1 }}>{a.name}</span>
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{a.reviewer || '—'}</span>
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{a.reviewed_at?.slice(0, 10)}</span>
            </div>
          ))}

          {/* High-risk sets */}
          {highRisk.length > 0 && (
            <>
              <div style={{ ...S.sectionTitle, marginTop: '24px', marginBottom: '10px' }}>High-Risk Rule Sets</div>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '10px' }}>These sets require 2+ approvals before publishing.</p>
              {highRisk.map(rs => (
                <div key={rs.id} style={{ ...S.card, cursor: 'default', flexDirection: 'column', alignItems: 'stretch' }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, flex: 1 }}>{rs.name}</span>
                    <span style={S.badge('#f59e0b')}>{rs.required_approvers_count} approvers required</span>
                    {rs.pending_versions > 0 && <span style={S.badge('#ef4444')}>{rs.pending_versions} pending</span>}
                  </div>
                  {editingSet === rs.id ? (
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '8px' }}>
                      <label style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Required approvers:</label>
                      <input type="number" min={1} max={10} value={editApprovers} onChange={e => setEditApprovers(parseInt(e.target.value, 10) || 1)} style={{ ...S.input, width: '60px', padding: '4px 8px' }} />
                      <button onClick={() => saveApprovers(rs.id)} style={{ ...S.btn('primary'), padding: '4px 10px', fontSize: '12px' }}>Save</button>
                      <button onClick={() => setEditingSet(null)} style={{ ...S.btn('ghost'), padding: '4px 10px', fontSize: '12px' }}>Cancel</button>
                    </div>
                  ) : (
                    <button onClick={() => { setEditingSet(rs.id); setEditApprovers(rs.required_approvers_count || 1); }} style={{ ...S.btn('ghost'), fontSize: '11px', padding: '3px 8px', marginTop: '6px', alignSelf: 'flex-start' }}>Edit</button>
                  )}
                </div>
              ))}
            </>
          )}

          {/* Configure approvers for all active sets */}
          <div style={{ ...S.sectionTitle, marginTop: '24px', marginBottom: '6px' }}>Configure Approvers</div>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '10px' }}>
            Set required approver counts per rule set to enforce 2-eyes or 4-eyes review.
          </p>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            Navigate to the <strong>Rule Sets</strong> tab, open any set, and update "Required Approvers" in its settings panel.
          </p>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Phase 6 — Rule Packs Tab (Marketplace + Workspace Packs)
// ===========================================================================
function PacksTab({ workspaceId, onNavigate }) {
  const [packs, setPacks] = useState([]);
  const [systemPacks, setSystemPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [installResult, setInstallResult] = useState(null);
  const [importJson, setImportJson] = useState('');
  const [importing, setImporting] = useState(false);
  const [sets, setSets] = useState([]);
  const [exportSetId, setExportSetId] = useState('');
  const [exporting, setExporting] = useState(false);
  const [expandedPack, setExpandedPack] = useState(null);
  const [installing, setInstalling] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      apiFetch(`/api/rules/workspaces/${workspaceId}/packs`),
      apiFetch(`/api/rules/workspaces/${workspaceId}/sets`),
      apiFetch('/api/rules/packs/system').catch(() => []),
    ]).then(([p, s, sys]) => { setPacks(p); setSets(s); setSystemPacks(sys); })
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const installSystemPack = async (slug) => {
    setErr(''); setMsg(''); setInstallResult(null); setInstalling(slug);
    try {
      const result = await apiFetch(`/api/rules/workspaces/${workspaceId}/packs/install/${slug}`, { method: 'POST' });
      setInstallResult({
        name: result.name,
        created: (result.imported_artifact_ids?.length || 0) + (result.imported_set_ids?.length || 0),
        skipped: (result.skipped_artifacts || 0) + (result.skipped_sets || 0),
      });
      load();
    } catch (e) { setErr(e.message); }
    setInstalling(null);
  };

  const importPack = async () => {
    setErr(''); setMsg(''); setImporting(true);
    try {
      const parsed = JSON.parse(importJson);
      const result = await apiFetch(`/api/rules/workspaces/${workspaceId}/packs/import`, {
        method: 'POST',
        body: JSON.stringify(parsed),
      });
      setMsg(`Imported "${result.name}" — ${result.imported_artifact_ids?.length || 0} artifacts, ${result.imported_set_ids?.length || 0} sets.`);
      setImportJson('');
      load();
    } catch (e) { setErr(e.message || 'Invalid JSON'); }
    setImporting(false);
  };

  const exportPack = async () => {
    if (!exportSetId) return;
    setErr(''); setMsg(''); setExporting(true);
    try {
      const result = await apiFetch(`/api/rules/workspaces/${workspaceId}/sets/${exportSetId}/export-pack`, { method: 'POST' });
      const blob = new Blob([JSON.stringify(result.contents, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${result.slug || 'pack'}.xavvypack.json`; a.click();
      URL.revokeObjectURL(url);
      setMsg(`Exported "${result.name}". Pack saved to workspace.`);
      load();
    } catch (e) { setErr(e.message); }
    setExporting(false);
  };

  const deletePack = async (packId) => {
    if (!window.confirm('Delete this pack?')) return;
    try {
      await apiFetch(`/api/rules/workspaces/${workspaceId}/packs/${packId}`, { method: 'DELETE' });
      setPacks(p => p.filter(x => x.id !== packId));
    } catch (e) { setErr(e.message); }
  };

  // Slugs of system packs installed in this workspace (workspace-scoped packs only)
  const installedSlugs = new Set(packs.map(p => p.slug));

  const PACK_CATEGORY_ICON = { 'insurance': '🏥', 'lending': '💰', 'itsm': '🖥️' };

  return (
    <div>
      {err && <div style={S.error}>{err}</div>}
      {msg && <div style={S.success}>{msg}</div>}
      {installResult && (
        <div style={{ ...S.success, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <span>
            ✓ Installed <strong>{installResult.name}</strong> — {installResult.created} artifact{installResult.created !== 1 ? 's' : ''} and rule sets added
            {installResult.skipped > 0 ? ` (${installResult.skipped} skipped, already existed)` : ''}.
          </span>
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            <button onClick={() => onNavigate?.('artifacts')} style={{ ...S.btn('ghost'), padding: '3px 10px', fontSize: '12px' }}>
              View Artifacts →
            </button>
            <button onClick={() => onNavigate?.('sets')} style={{ ...S.btn('ghost'), padding: '3px 10px', fontSize: '12px' }}>
              View Rule Sets →
            </button>
            <button onClick={() => setInstallResult(null)} style={{ background: 'none', border: 'none', color: 'var(--success)', cursor: 'pointer', fontSize: '14px', padding: '0 4px' }}>✕</button>
          </div>
        </div>
      )}

      {/* System Packs Marketplace */}
      <div style={{ marginBottom: '28px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
          <div style={S.sectionTitle}>System Packs</div>
          <span style={S.badge('#7c7cff')}>xavvy</span>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>First-party rule bundles ready to install</span>
        </div>
        {loading && <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Loading…</p>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px' }}>
          {systemPacks.map(sp => {
            const tags = sp.manifest?.tags || [];
            const icon = Object.entries(PACK_CATEGORY_ICON).find(([k]) => tags.includes(k))?.[1] || '📦';
            const isInstalled = installedSlugs.has(sp.slug);
            return (
              <div key={sp.id} style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ fontSize: '28px' }}>{icon}</div>
                <div style={{ fontWeight: 700, fontSize: '14px' }}>{sp.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', flex: 1 }}>{sp.description}</div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
                  {sp.manifest?.artifact_count != null && <span style={S.badge('#71717a')}>{sp.manifest.artifact_count} artifacts</span>}
                  {sp.manifest?.set_count != null && <span style={S.badge('#71717a')}>{sp.manifest.set_count} sets</span>}
                </div>
                {isInstalled ? (
                  <button disabled style={{ ...S.btn('ghost'), opacity: 0.6, cursor: 'default' }}>✓ Installed</button>
                ) : (
                  <button
                    onClick={() => installSystemPack(sp.slug)}
                    disabled={installing === sp.slug}
                    style={{ ...S.btn('primary') }}
                  >
                    {installing === sp.slug ? 'Installing…' : '⬇ Install'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div style={S.divider} />

      {/* Export / Import */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '28px' }}>
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px' }}>
          <div style={S.sectionTitle}>Export Rule Set as Pack</div>
          <label style={S.label}>Rule Set to Export</label>
          <select value={exportSetId} onChange={e => setExportSetId(e.target.value)} style={{ ...S.select, width: '100%', marginBottom: '10px' }}>
            <option value="">— Select —</option>
            {sets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button onClick={exportPack} disabled={!exportSetId || exporting} style={{ ...S.btn('primary'), width: '100%' }}>
            {exporting ? 'Exporting…' : '⬇ Export .xavvypack'}
          </button>
        </div>

        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px' }}>
          <div style={S.sectionTitle}>Import Pack (JSON)</div>
          <textarea
            value={importJson}
            onChange={e => setImportJson(e.target.value)}
            placeholder={'{\n  "name": "My Pack",\n  "slug": "my-pack",\n  "contents": { "artifacts": [], "sets": [] }\n}'}
            style={{ ...S.textarea, minHeight: '80px', marginBottom: '10px' }}
          />
          <button onClick={importPack} disabled={!importJson.trim() || importing} style={{ ...S.btn('primary'), width: '100%' }}>
            {importing ? 'Importing…' : '⬆ Import Pack'}
          </button>
        </div>
      </div>

      <div style={S.sectionTitle}>Workspace Packs {loading && '…'}</div>
      {!loading && packs.length === 0 && <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>No workspace packs yet. Install a system pack or export one above.</p>}
      {packs.map(p => (
        <div key={p.id}>
          <div
            style={{ ...S.card, cursor: 'pointer' }}
            onClick={() => setExpandedPack(expandedPack === p.id ? null : p.id)}
          >
            <span style={{ fontWeight: 700, flex: 1 }}>{p.name}</span>
            <span style={S.badge('#71717a')}>v{p.version}</span>
            {p.manifest?.artifact_count != null && <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{p.manifest.artifact_count} artifacts, {p.manifest.set_count} sets</span>}
            <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{p.created_at?.slice(0, 10)}</span>
            <button onClick={e => { e.stopPropagation(); deletePack(p.id); }} style={{ ...S.btn('danger'), padding: '4px 10px', fontSize: '12px' }}>Delete</button>
          </div>
          {expandedPack === p.id && (
            <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '14px', marginBottom: '8px' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Slug: <code style={{ color: 'var(--text-primary)' }}>{p.slug}</code> · {p.description || 'No description'}</div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Rules({ workspaceId, onBack }) {
  const [tab, setTab] = useState('artifacts');

  return (
    <div style={S.shell}>
      <div style={S.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {onBack && <button onClick={onBack} style={{ ...S.btn('ghost'), padding: '4px 10px' }}>← Back</button>}
          <h1 style={S.title}>⚖️ Rules Engine</h1>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginLeft: '4px' }}>workspace: {workspaceId}</span>
        </div>
        <div style={S.tabs}>
          {[
            ['artifacts', '📋 Artifacts'],
            ['sets', '📦 Rule Sets'],
            ['simulator', '🧪 Simulator'],
            ['traces', '🔍 Trace Explorer'],
            ['ab_tests', '🔬 A/B Tests'],
            ['governance', '✅ Governance'],
            ['packs', '🎁 Packs'],
          ].map(([id, label]) => (
            <button key={id} style={S.tab(tab === id)} onClick={() => setTab(id)}>{label}</button>
          ))}
        </div>
      </div>

      <div style={S.body}>
        {tab === 'artifacts' && <ArtifactsTab workspaceId={workspaceId} />}
        {tab === 'sets' && <SetsTab workspaceId={workspaceId} />}
        {tab === 'simulator' && <SimulatorTab workspaceId={workspaceId} />}
        {tab === 'traces' && <TracesTab workspaceId={workspaceId} />}
        {tab === 'ab_tests' && <ABTestsTab workspaceId={workspaceId} />}
        {tab === 'governance' && <GovernanceTab workspaceId={workspaceId} />}
        {tab === 'packs' && <PacksTab workspaceId={workspaceId} onNavigate={setTab} />}
      </div>
    </div>
  );
}
